"use client";

// Shared production navigation surface for both Web and Shop runtimes.

import {
    Fragment,
    useCallback,
    useEffect,
    useId,
    useRef,
    useState,
} from "react";
import type {
    CSSProperties,
    KeyboardEvent,
    ReactNode,
} from "react";
import type {
    SiteNavigationSettings,
    SiteNavigationThemeStyle,
    SiteNavigationVariant,
} from "./siteNavigation";
import { SITE_NAVIGATION_MOBILE_BREAKPOINT_PX } from "./siteNavigation";
import { siteNavigationKeyboardAction } from "./siteNavigationKeyboard";
export { siteNavigationKeyboardAction } from "./siteNavigationKeyboard";
import {
    LAGUNA_CLASSIC_LINK_GROUP_SIZE,
    LAGUNA_CLASSIC_OVERFLOW_COLUMN_LIMIT,
    LAGUNA_CLASSIC_VISIBLE_COLUMN_LIMIT,
    siteNavigationLagunaClassicColumns,
    siteNavigationLagunaClassicLinkGroups,
    siteNavigationLagunaClassicRoot,
    siteNavigationLagunaClassicSaleItem,
} from "./siteNavigationLagunaClassic";
import type { SiteNavigationItem } from "./siteBuilderTypes";

const OPEN_DELAY_MS = 120;
const SWITCH_DELAY_MS = 80;
const CLOSE_DELAY_MS = 260;

type NavigationTimer = Readonly<{
    id: number;
    ownerWindow: Window;
}>;

function navigationOwnerDocument(element: Element | null): Document | null {
    return element?.ownerDocument ?? null;
}

function navigationOwnerWindow(element: Element | null): Window | null {
    return navigationOwnerDocument(element)?.defaultView ?? null;
}

function navigationTargetIsNode(
    ownerDocument: Document | null,
    target: EventTarget | null,
): target is Node {
    const NodeConstructor = ownerDocument?.defaultView?.Node;

    return NodeConstructor !== undefined && target instanceof NodeConstructor;
}

function navigationTargetIsElement(
    ownerDocument: Document | null,
    target: EventTarget | null,
): target is Element {
    const ElementConstructor = ownerDocument?.defaultView?.Element;

    return ElementConstructor !== undefined && target instanceof ElementConstructor;
}

function navigationSetTimeout(
    element: Element | null,
    callback: () => void,
    delay: number,
): NavigationTimer | null {
    const ownerWindow = navigationOwnerWindow(element);

    if (!ownerWindow) return null;

    return {
        id: ownerWindow.setTimeout(callback, delay),
        ownerWindow,
    };
}

function navigationClearTimeout(timer: NavigationTimer | null): void {
    if (!timer) return;

    timer.ownerWindow.clearTimeout(timer.id);
}

function classes(...values: Array<string | false | null | undefined>): string {
    return values.filter(Boolean).join(" ");
}

function safeId(value: string): string {
    return value.replace(/[^a-zA-Z0-9_-]/gu, "-");
}

function itemHref(item: SiteNavigationItem): string | null {
    return item.href?.trim() || null;
}

function itemContainsUuid(item: SiteNavigationItem, uuid: string | undefined): boolean {
    return item.uuid === uuid || item.children.some((child) => itemContainsUuid(child, uuid));
}

function itemOpenPath(
    items: readonly SiteNavigationItem[],
    uuid: string | undefined,
): string[] {
    if (!uuid) return [];

    for (const item of items) {
        if (item.uuid === uuid) {
            return item.children.length > 0 ? [item.uuid] : [];
        }

        const childPath = itemOpenPath(item.children, uuid);

        if (childPath.length > 0 || item.children.some((child) => child.uuid === uuid)) {
            return [item.uuid, ...childPath];
        }
    }

    return [];
}

export type SiteNavigationLabels = Readonly<{
    closeSubmenu?: string;
    navigation: string;
    openSubmenu?: string;
    recommended?: string;
    showAll: string;
}>;

function submenuToggleLabel(
    labels: SiteNavigationLabels,
    item: SiteNavigationItem,
    expanded: boolean,
): string {
    const action = expanded ? labels.closeSubmenu : labels.openSubmenu;

    return `${action ?? labels.navigation}: ${item.label}`;
}

export type SiteNavigationRenderLinkInput = Readonly<{
    ariaCurrent?: "page";
    children: ReactNode;
    className: string;
    href: string;
    id?: string;
    item: SiteNavigationItem;
    onClick?: () => void;
}>;

export type SiteNavigationRenderLink = (
    input: SiteNavigationRenderLinkInput,
) => ReactNode;

function renderNavigationItemLink(
    renderLink: SiteNavigationRenderLink,
    input: Omit<SiteNavigationRenderLinkInput, "href">,
): ReactNode {
    const href = itemHref(input.item);

    if (href !== null) {
        return renderLink({
            ...input,
            href,
        });
    }

    return (
        <span
            aria-current={input.ariaCurrent}
            className={input.className}
            data-site-navigation-item-uuid={input.item.uuid}
            data-site-navigation-label="true"
            id={input.id}
        >
            {input.children}
        </span>
    );
}

export type SiteNavigationProps = Readonly<{
    activeItemUuid?: string;
    className?: string;
    includeStyles?: boolean;
    items: readonly SiteNavigationItem[];
    labels: SiteNavigationLabels;
    mode: "desktop" | "mobile";
    preservePanelOnLinkClick?: boolean;
    previewOpenItemUuid?: string;
    readOnly?: boolean;
    renderLink?: SiteNavigationRenderLink;
    settings: SiteNavigationSettings;
    staticPreview?: boolean;
    style?: CSSProperties | SiteNavigationThemeStyle;
}>;

function DefaultLink({
    ariaCurrent,
    children,
    className,
    href,
    id,
    item,
    onClick,
}: SiteNavigationRenderLinkInput) {
    return (
        <a
            aria-current={ariaCurrent}
            className={className}
            data-navigation-item-type={item.type}
            data-site-navigation-item-uuid={item.uuid}
            data-site-navigation-link-owner="true"
            href={href}
            id={id}
            onClick={onClick}
            rel={item.openInNewTab ? "noopener noreferrer" : undefined}
            target={item.openInNewTab ? "_blank" : undefined}
        >
            {children}
        </a>
    );
}

function isCleanNavigationVariant(
    variant: SiteNavigationVariant,
): variant is Extract<
    SiteNavigationVariant,
    | "minimalReveal"
    | "boldUnderline"
    | "editorialRule"
    | "playfulFrame"
    | "neonLine"
> {
    return variant === "minimalReveal"
        || variant === "boldUnderline"
        || variant === "editorialRule"
        || variant === "playfulFrame"
        || variant === "neonLine";
}

function isBorderedDropdownVariant(
    variant: SiteNavigationVariant,
): variant is Extract<
    SiteNavigationVariant,
    "borderedDropdown"
> {
    return variant === "borderedDropdown";
}

function isImageCategoryStripVariant(
    variant: SiteNavigationVariant,
): variant is Extract<
    SiteNavigationVariant,
    "imageCategoryStrip"
> {
    return variant === "imageCategoryStrip";
}

function isCompactTextNavigationVariant(
    variant: SiteNavigationVariant,
): variant is Extract<
    SiteNavigationVariant,
    | "borderedDropdown"
    | "monochromeDropdown"
> {
    return isBorderedDropdownVariant(variant) || variant === "monochromeDropdown";
}

function neutralNavigationSignature(
    variant: SiteNavigationVariant,
): string | undefined {
    if (isBorderedDropdownVariant(variant)) return "bordered:text-list:compact";
    if (isImageCategoryStripVariant(variant)) return "visual:image-title-strip";
    if (variant === "monochromeDropdown") return "monochrome:text-list:compact";

    return undefined;
}

function Marker({
    index,
    item,
    variant,
}: {
    index: number;
    item: SiteNavigationItem;
    variant: SiteNavigationVariant;
}) {
    if (isCleanNavigationVariant(variant)) {
        return null;
    }

    if (isCompactTextNavigationVariant(variant) || isImageCategoryStripVariant(variant)) {
        return null;
    }

    if (variant === "industrialBar") {
        return (
            <span aria-hidden="true" className="site-navigation-marker site-navigation-marker-industrial">
                {String(index + 1).padStart(2, "0")}
            </span>
        );
    }

    if (variant === "cinematicOverlay") {
        return (
            <span aria-hidden="true" className="site-navigation-marker site-navigation-marker-cinematic">
                {String(index + 1).padStart(2, "0")}
            </span>
        );
    }

    if (variant === "audienceMega") {
        return <span aria-hidden="true" className="site-navigation-marker site-navigation-marker-audience" />;
    }

    if (variant === "editorialOverlay") {
        return (
            <span aria-hidden="true" className="site-navigation-marker site-navigation-marker-editorial">
                {String(index + 1).padStart(2, "0")}
            </span>
        );
    }

    if (variant === "technicalIndex" || variant === "productStory") {
        return (
            <span aria-hidden="true" className="site-navigation-marker site-navigation-marker-index">
                {String(index + 1).padStart(2, "0")}
            </span>
        );
    }

    if (variant === "routineMega") {
        return (
            <span aria-hidden="true" className="site-navigation-marker site-navigation-marker-step">
                {index + 1}
            </span>
        );
    }

    if (variant === "roomPlanner") {
        return <span aria-hidden="true" className="site-navigation-marker site-navigation-marker-room">⌂</span>;
    }

    if (variant === "collectionConcierge") {
        return <span aria-hidden="true" className="site-navigation-marker site-navigation-marker-diamond" />;
    }

    if (variant === "groceryHub" || variant === "categoryPills") {
        return <span aria-hidden="true" className="site-navigation-marker site-navigation-marker-dot" />;
    }

    if (variant === "departmentMega") {
        return (
            <span aria-hidden="true" className="site-navigation-marker site-navigation-marker-code">
                {item.label.trim().slice(0, 2)}
            </span>
        );
    }

    return null;
}

function Branch({
    activeItemUuid,
    depth = 0,
    index,
    item,
    renderLink,
    variant,
}: {
    activeItemUuid?: string;
    depth?: number;
    index: number;
    item: SiteNavigationItem;
    renderLink: SiteNavigationRenderLink;
    variant: SiteNavigationVariant;
}) {
    const active = itemContainsUuid(item, activeItemUuid);
    const link = renderNavigationItemLink(renderLink, {
        ariaCurrent: item.uuid === activeItemUuid ? "page" : undefined,
        children: (
            <>
                {depth === 0 || variant === "technicalIndex" || variant === "industrialBar" ? (
                    <Marker index={index} item={item} variant={variant} />
                ) : null}
                <span className="site-navigation-branch-label">{item.label}</span>
                {item.children.length > 0 ? <span aria-hidden="true" className="site-navigation-branch-chevron" /> : null}
            </>
        ),
        className: classes(
            "site-navigation-branch-link",
            active && "is-active",
        ),
        item,
    });

    return (
        <li
            className={classes(
                "site-navigation-branch",
                `site-navigation-depth-${Math.min(depth, 3)}`,
            )}
            data-navigation-depth={depth}
            data-navigation-item-type={item.type}
        >
            {link}
            {item.children.length > 0 ? (
                <ul className="site-navigation-children">
                    {item.children.map((child, childIndex) => (
                        <Branch
                            activeItemUuid={activeItemUuid}
                            depth={depth + 1}
                            index={childIndex}
                            item={child}
                            key={child.uuid}
                            renderLink={renderLink}
                            variant={variant}
                        />
                    ))}
                </ul>
            ) : null}
        </li>
    );
}

function FeatureCard({
    item,
    renderLink,
    variant,
}: {
    item: SiteNavigationItem;
    renderLink: SiteNavigationRenderLink;
    variant: SiteNavigationVariant;
}) {
    const featured = item.children[item.children.length - 1] ?? item;
    const tags = featured.children.slice(0, 3);

    return renderNavigationItemLink(renderLink, {
        children: (
            <>
                <span className="site-navigation-feature-kicker">
                    {String(item.children.length).padStart(2, "0")}
                    <span aria-hidden="true">↗</span>
                </span>
                <span className="site-navigation-feature-copy">
                    <strong>{featured.label}</strong>
                    {tags.length > 0 ? (
                        <span className="site-navigation-feature-tags">
                            {tags.map((tag) => <span key={tag.uuid}>{tag.label}</span>)}
                        </span>
                    ) : null}
                </span>
            </>
        ),
        className: `site-navigation-feature site-navigation-feature-${variant}`,
        item: featured,
    });
}

function PanelHeading({
    item,
    labels,
    renderLink,
    variant,
}: {
    item: SiteNavigationItem;
    labels: SiteNavigationLabels;
    renderLink: SiteNavigationRenderLink;
    variant: SiteNavigationVariant;
}) {
    return (
        <div className="site-navigation-panel-heading">
            <div>
                <span className="site-navigation-panel-eyebrow">{String(item.children.length).padStart(2, "0")}</span>
                <strong>{item.label}</strong>
            </div>
            {itemHref(item) !== null ? renderNavigationItemLink(renderLink, {
                children: <>{labels.showAll}<span aria-hidden="true"> →</span></>,
                className: "site-navigation-show-all",
                item,
            }) : null}
            {variant === "technicalIndex" || variant === "industrialBar" ? (
                <span aria-hidden="true" className="site-navigation-index-rule" />
            ) : null}
        </div>
    );
}

function Branches({
    activeItemUuid,
    children,
    renderLink,
    variant,
}: {
    activeItemUuid?: string;
    children: readonly SiteNavigationItem[];
    renderLink: SiteNavigationRenderLink;
    variant: SiteNavigationVariant;
}) {
    return (
        <ul className={`site-navigation-tree site-navigation-tree-${variant}`}>
            {children.map((child, index) => (
                <Branch
                    activeItemUuid={activeItemUuid}
                    index={index}
                    item={child}
                    key={child.uuid}
                    renderLink={renderLink}
                    variant={variant}
                />
            ))}
        </ul>
    );
}

function CompactDropdownBranch({
    activeItemUuid,
    depth = 0,
    item,
    renderLink,
    variant,
}: {
    activeItemUuid?: string;
    depth?: number;
    item: SiteNavigationItem;
    renderLink: SiteNavigationRenderLink;
    variant: "borderedDropdown" | "monochromeDropdown";
}) {
    return (
        <li
            className="site-navigation-compact-item"
            data-navigation-depth={depth}
            data-navigation-item-type={item.type}
        >
            {renderNavigationItemLink(renderLink, {
                ariaCurrent: item.uuid === activeItemUuid ? "page" : undefined,
                children: <span className="site-navigation-compact-label">{item.label}</span>,
                className: classes(
                    "site-navigation-compact-link",
                    itemContainsUuid(item, activeItemUuid) && "is-active",
                ),
                item,
            })}
            {item.children.length > 0 ? (
                <ul className="site-navigation-compact-children">
                    {item.children.map((child) => (
                        <CompactDropdownBranch
                            activeItemUuid={activeItemUuid}
                            depth={depth + 1}
                            item={child}
                            key={child.uuid}
                            renderLink={renderLink}
                            variant={variant}
                        />
                    ))}
                </ul>
            ) : null}
        </li>
    );
}

function CompactDropdownPanel({
    activeItemUuid,
    item,
    renderLink,
    variant,
}: {
    activeItemUuid?: string;
    item: SiteNavigationItem;
    renderLink: SiteNavigationRenderLink;
    variant: "borderedDropdown" | "monochromeDropdown";
}) {
    return (
        <div
            className={`site-navigation-compact-dropdown site-navigation-compact-dropdown-${variant}`}
            data-navigation-anatomy="compact-bordered-text-dropdown"
            data-navigation-signature={neutralNavigationSignature(variant)}
        >
            <ul className="site-navigation-compact-list">
                {item.children.map((child) => (
                    <CompactDropdownBranch
                        activeItemUuid={activeItemUuid}
                        item={child}
                        key={child.uuid}
                        renderLink={renderLink}
                        variant={variant}
                    />
                ))}
            </ul>
        </div>
    );
}

function ImageStripNestedBranch({
    activeItemUuid,
    depth,
    item,
    renderLink,
}: {
    activeItemUuid?: string;
    depth: number;
    item: SiteNavigationItem;
    renderLink: SiteNavigationRenderLink;
}) {
    return (
        <li
            className="site-navigation-image-strip-nested-item"
            data-navigation-depth={depth}
        >
            {renderNavigationItemLink(renderLink, {
                ariaCurrent: item.uuid === activeItemUuid ? "page" : undefined,
                children: item.label,
                className: classes(
                    "site-navigation-image-strip-nested-link",
                    itemContainsUuid(item, activeItemUuid) && "is-active",
                ),
                item,
            })}
            {item.children.length > 0 ? (
                <ul className="site-navigation-image-strip-nested-list">
                    {item.children.map((child) => (
                        <ImageStripNestedBranch
                            activeItemUuid={activeItemUuid}
                            depth={depth + 1}
                            item={child}
                            key={child.uuid}
                            renderLink={renderLink}
                        />
                    ))}
                </ul>
            ) : null}
        </li>
    );
}

function ImageStripItem({
    activeItemUuid,
    index,
    item,
    renderLink,
}: {
    activeItemUuid?: string;
    index: number;
    item: SiteNavigationItem;
    renderLink: SiteNavigationRenderLink;
}) {
    return (
        <li className="site-navigation-image-strip-item" data-navigation-strip-item={index + 1}>
            {renderNavigationItemLink(renderLink, {
                ariaCurrent: item.uuid === activeItemUuid ? "page" : undefined,
                children: (
                    <>
                        {item.imageUrl ? (
                            <span className="site-navigation-image-strip-visual">
                                <img alt="" loading="lazy" src={item.imageUrl} />
                            </span>
                        ) : null}
                        <strong className="site-navigation-image-strip-title">{item.label}</strong>
                    </>
                ),
                className: classes(
                    "site-navigation-image-strip-link",
                    itemContainsUuid(item, activeItemUuid) && "is-active",
                ),
                item,
            })}
            {item.children.length > 0 ? (
                <ul className="site-navigation-image-strip-links">
                    {item.children.map((child) => (
                        <ImageStripNestedBranch
                            activeItemUuid={activeItemUuid}
                            depth={1}
                            item={child}
                            key={child.uuid}
                            renderLink={renderLink}
                        />
                    ))}
                </ul>
            ) : null}
        </li>
    );
}

function ImageCategoryStripPanel({
    activeItemUuid,
    item,
    renderLink,
}: {
    activeItemUuid?: string;
    item: SiteNavigationItem;
    renderLink: SiteNavigationRenderLink;
}) {
    return (
        <div
            className="site-navigation-image-category-strip"
            data-navigation-anatomy="full-width-image-title-strip"
            data-navigation-signature="visual:image-title-strip"
        >
            <ul className="site-navigation-image-strip-list">
                {item.children.map((child, index) => (
                    <ImageStripItem
                        activeItemUuid={activeItemUuid}
                        index={index}
                        item={child}
                        key={child.uuid}
                        renderLink={renderLink}
                    />
                ))}
            </ul>
        </div>
    );
}

function LagunaClassicMarker({ item }: { item: SiteNavigationItem }) {
    return (
        <span
            aria-hidden="true"
            className="site-navigation-laguna-classic-marker"
        >
            {item.label.trim().slice(0, 2)}
        </span>
    );
}

function LagunaClassicPanelLink({
    activeItemUuid,
    item,
    renderLink,
}: {
    activeItemUuid?: string;
    item: SiteNavigationItem;
    renderLink: SiteNavigationRenderLink;
}) {
    return (
        <div
            className="site-navigation-laguna-classic-branch"
            data-navigation-depth={0}
        >
            {renderNavigationItemLink(renderLink, {
                ariaCurrent: item.uuid === activeItemUuid ? "page" : undefined,
                children: (
                    <>
                        <LagunaClassicMarker item={item} />
                        <span className="site-navigation-laguna-classic-label">{item.label}</span>
                    </>
                ),
                className: classes(
                    "site-navigation-laguna-classic-link",
                    itemContainsUuid(item, activeItemUuid) && "is-active",
                    siteNavigationLagunaClassicSaleItem(item) && "is-sale",
                ),
                item,
            })}
        </div>
    );
}

function LagunaClassicPanel({
    activeItemUuid,
    item,
    labels,
    renderLink,
}: {
    activeItemUuid?: string;
    item: SiteNavigationItem;
    labels: SiteNavigationLabels;
    renderLink: SiteNavigationRenderLink;
}) {
    const columns = siteNavigationLagunaClassicColumns(
        item,
        labels.recommended ?? item.label,
    );
    const visibleColumns = columns.slice(0, LAGUNA_CLASSIC_VISIBLE_COLUMN_LIMIT);
    const hasSplitLinks = visibleColumns.some((column) => (
        column.links.length > LAGUNA_CLASSIC_LINK_GROUP_SIZE
    ));
    const columnCount = Math.max(1, visibleColumns.length);

    return (
        <div
            className="site-navigation-laguna-classic-panel"
            data-navigation-anatomy="laguna-classic-glass-columns"
        >
            <div
                className={classes(
                    "site-navigation-laguna-classic-grid",
                    `site-navigation-laguna-classic-grid-${columnCount}`,
                    visibleColumns.length === 1 && hasSplitLinks && "is-split",
                )}
            >
                {visibleColumns.map((column, columnIndex) => {
                    const linkGroups = siteNavigationLagunaClassicLinkGroups(column.links);

                    return (
                        <section
                            className="site-navigation-laguna-classic-column"
                            data-navigation-column={column.key}
                            key={column.key}
                        >
                            {columnIndex > 0 ? (
                                <span
                                    aria-hidden="true"
                                    className="site-navigation-laguna-classic-divider"
                                />
                            ) : null}
                            {column.title ? (
                                column.titleItem && itemHref(column.titleItem) !== null
                                    ? renderNavigationItemLink(renderLink, {
                                        children: column.title,
                                        className: "site-navigation-laguna-classic-title",
                                        item: column.titleItem,
                                    })
                                    : <h3 className="site-navigation-laguna-classic-title">{column.title}</h3>
                            ) : null}
                            <div
                                className={classes(
                                    "site-navigation-laguna-classic-groups",
                                    linkGroups.length > 1 && "is-split",
                                    column.title && "has-title",
                                )}
                            >
                                {linkGroups.map((group, groupIndex) => (
                                    <div
                                        className="site-navigation-laguna-classic-group"
                                        key={`${column.key}:group:${groupIndex}`}
                                    >
                                        {group.map((link) => (
                                            <LagunaClassicPanelLink
                                                activeItemUuid={activeItemUuid}
                                                item={link}
                                                key={link.uuid}
                                                renderLink={renderLink}
                                            />
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </section>
                    );
                })}
            </div>
            {columns.length > LAGUNA_CLASSIC_VISIBLE_COLUMN_LIMIT ? (
                <div
                    className="site-navigation-laguna-classic-overflow"
                    data-navigation-anatomy="laguna-classic-overflow-strip"
                >
                    {columns
                        .slice(
                            LAGUNA_CLASSIC_VISIBLE_COLUMN_LIMIT,
                            LAGUNA_CLASSIC_OVERFLOW_COLUMN_LIMIT,
                        )
                        .map((column) => (
                            <Fragment key={column.key}>
                                {column.titleItem && itemHref(column.titleItem) !== null
                                    ? renderNavigationItemLink(renderLink, {
                                        children: column.title ?? column.titleItem.label,
                                        className: "site-navigation-laguna-classic-overflow-link",
                                        item: column.titleItem,
                                    })
                                    : <span className="site-navigation-laguna-classic-overflow-label">{column.title}</span>}
                            </Fragment>
                        ))}
                </div>
            ) : null}
        </div>
    );
}

function PanelComposition({
    activeItemUuid,
    item,
    labels,
    renderLink,
    variant,
}: {
    activeItemUuid?: string;
    item: SiteNavigationItem;
    labels: SiteNavigationLabels;
    renderLink: SiteNavigationRenderLink;
    variant: SiteNavigationVariant;
}) {
    const first = item.children[0] ?? null;
    const rest = item.children.slice(1);
    const commonHeading = (
        <PanelHeading item={item} labels={labels} renderLink={renderLink} variant={variant} />
    );

    if (isBorderedDropdownVariant(variant)) {
        return (
            <CompactDropdownPanel
                activeItemUuid={activeItemUuid}
                item={item}
                renderLink={renderLink}
                variant="borderedDropdown"
            />
        );
    }

    if (variant === "monochromeDropdown") {
        return (
            <CompactDropdownPanel
                activeItemUuid={activeItemUuid}
                item={item}
                renderLink={renderLink}
                variant="monochromeDropdown"
            />
        );
    }

    if (isImageCategoryStripVariant(variant)) {
        return (
            <ImageCategoryStripPanel
                activeItemUuid={activeItemUuid}
                item={item}
                renderLink={renderLink}
            />
        );
    }

    if (isCleanNavigationVariant(variant)) {
        return (
            <div
                className={`site-navigation-clean site-navigation-clean-${variant}`}
                data-navigation-anatomy="clean-link-columns"
            >
                <section className="site-navigation-panel-main">
                    <div className="site-navigation-clean-heading">
                        <strong>{item.label}</strong>
                        {itemHref(item) !== null ? renderNavigationItemLink(renderLink, {
                            children: <>{labels.showAll}<span aria-hidden="true"> →</span></>,
                            className: "site-navigation-clean-show-all",
                            item,
                        }) : null}
                    </div>
                    <Branches
                        activeItemUuid={activeItemUuid}
                        renderLink={renderLink}
                        variant={variant}
                    >
                        {item.children}
                    </Branches>
                </section>
            </div>
        );
    }

    if (variant === "lagunaClassic") {
        return (
            <LagunaClassicPanel
                activeItemUuid={activeItemUuid}
                item={item}
                labels={labels}
                renderLink={renderLink}
            />
        );
    }

    if (variant === "departmentMega") {
        return (
            <div className="site-navigation-composition site-navigation-department">
                <aside className="site-navigation-department-rail">
                    <span className="site-navigation-rail-title">{item.label}</span>
                    {item.children.slice(0, 7).map((child, index) => (
                        <Fragment key={child.uuid}>
                            {renderNavigationItemLink(renderLink, {
                                children: <><Marker index={index} item={child} variant={variant} /><span>{child.label}</span></>,
                                className: "site-navigation-rail-link",
                                item: child,
                            })}
                        </Fragment>
                    ))}
                </aside>
                <section className="site-navigation-panel-main">
                    {commonHeading}
                    <Branches activeItemUuid={activeItemUuid} renderLink={renderLink} variant={variant}>
                        {item.children}
                    </Branches>
                </section>
                <FeatureCard item={item} renderLink={renderLink} variant={variant} />
            </div>
        );
    }

    if (variant === "audienceMega") {
        return (
            <div
                className="site-navigation-composition site-navigation-audience"
                data-navigation-anatomy="audience-lead-and-columns"
            >
                {first ? (
                    <aside className="site-navigation-audience-lead">
                        <ul>
                            <Branch
                                activeItemUuid={activeItemUuid}
                                index={0}
                                item={first}
                                renderLink={renderLink}
                                variant={variant}
                            />
                        </ul>
                    </aside>
                ) : null}
                <section className="site-navigation-panel-main">
                    {commonHeading}
                    <Branches
                        activeItemUuid={activeItemUuid}
                        renderLink={renderLink}
                        variant={variant}
                    >
                        {first ? rest : item.children}
                    </Branches>
                </section>
            </div>
        );
    }

    if (variant === "roomPlanner") {
        return (
            <div
                className="site-navigation-room-planner"
                data-navigation-anatomy="room-card-planner"
            >
                <section className="site-navigation-panel-main">
                    {commonHeading}
                    <Branches
                        activeItemUuid={activeItemUuid}
                        renderLink={renderLink}
                        variant={variant}
                    >
                        {item.children}
                    </Branches>
                </section>
            </div>
        );
    }

    if (variant === "routineMega") {
        return (
            <div
                className="site-navigation-routine-path"
                data-navigation-anatomy="routine-step-path"
            >
                <section className="site-navigation-panel-main">
                    {commonHeading}
                    <Branches
                        activeItemUuid={activeItemUuid}
                        renderLink={renderLink}
                        variant={variant}
                    >
                        {item.children}
                    </Branches>
                </section>
            </div>
        );
    }

    if (variant === "groceryHub") {
        const hasAisleSplit = item.children.length > 5;
        const aisleItems = hasAisleSplit ? item.children.slice(0, 5) : [];
        const catalogItems = hasAisleSplit ? item.children.slice(5) : item.children;

        return (
            <div
                className={classes(
                    "site-navigation-grocery-hub",
                    !hasAisleSplit && "is-compact",
                )}
                data-navigation-anatomy="grocery-aisles-and-catalog"
                data-navigation-grocery-layout={hasAisleSplit ? "aisles" : "compact"}
            >
                {hasAisleSplit ? (
                    <aside className="site-navigation-grocery-aisles">
                        <ul>
                            {aisleItems.map((child, index) => (
                                <Branch
                                    activeItemUuid={activeItemUuid}
                                    index={index}
                                    item={child}
                                    key={child.uuid}
                                    renderLink={renderLink}
                                    variant={variant}
                                />
                            ))}
                        </ul>
                    </aside>
                ) : null}
                <section className="site-navigation-panel-main">
                    {commonHeading}
                    <Branches
                        activeItemUuid={activeItemUuid}
                        renderLink={renderLink}
                        variant={variant}
                    >
                        {catalogItems}
                    </Branches>
                </section>
            </div>
        );
    }

    if (variant === "categoryPills") {
        return (
            <div
                className="site-navigation-pill-hub"
                data-navigation-anatomy="category-pill-clusters"
            >
                <section className="site-navigation-panel-main">
                    {commonHeading}
                    <ul className="site-navigation-pill-clusters">
                        {item.children.map((child, index) => (
                            <Branch
                                activeItemUuid={activeItemUuid}
                                index={index}
                                item={child}
                                key={child.uuid}
                                renderLink={renderLink}
                                variant={variant}
                            />
                        ))}
                    </ul>
                </section>
            </div>
        );
    }

    if (variant === "editorialOverlay") {
        return (
            <div className="site-navigation-composition site-navigation-editorial">
                <section className="site-navigation-panel-main">
                    {commonHeading}
                    <div className="site-navigation-editorial-grid">
                        {first ? (
                            <div className="site-navigation-editorial-lead">
                                <Branch activeItemUuid={activeItemUuid} index={0} item={first} renderLink={renderLink} variant={variant} />
                            </div>
                        ) : null}
                        <Branches activeItemUuid={activeItemUuid} renderLink={renderLink} variant={variant}>
                            {rest}
                        </Branches>
                    </div>
                </section>
                <FeatureCard item={item} renderLink={renderLink} variant={variant} />
            </div>
        );
    }

    if (variant === "productStory") {
        return (
            <div className="site-navigation-composition site-navigation-story">
                {first ? <FeatureCard item={{ ...item, children: [first] }} renderLink={renderLink} variant={variant} /> : null}
                <section className="site-navigation-panel-main">
                    {commonHeading}
                    <Branches activeItemUuid={activeItemUuid} renderLink={renderLink} variant={variant}>
                        {item.children}
                    </Branches>
                </section>
            </div>
        );
    }

    if (variant === "collectionConcierge") {
        return (
            <div className="site-navigation-composition site-navigation-concierge">
                <section className="site-navigation-panel-main">
                    {commonHeading}
                    <Branches activeItemUuid={activeItemUuid} renderLink={renderLink} variant={variant}>
                        {item.children}
                    </Branches>
                </section>
                <FeatureCard item={item} renderLink={renderLink} variant={variant} />
            </div>
        );
    }

    if (variant === "industrialBar") {
        return (
            <div
                className="site-navigation-composition site-navigation-industrial"
                data-navigation-anatomy="industrial-index-bar"
            >
                <section className="site-navigation-panel-main">
                    {commonHeading}
                    <Branches
                        activeItemUuid={activeItemUuid}
                        renderLink={renderLink}
                        variant={variant}
                    >
                        {item.children}
                    </Branches>
                </section>
                <aside className="site-navigation-industrial-aside">
                    <span aria-hidden="true" className="site-navigation-industrial-count">
                        {String(item.children.length).padStart(2, "0")} / {item.label}
                    </span>
                    <FeatureCard item={item} renderLink={renderLink} variant={variant} />
                </aside>
            </div>
        );
    }

    if (variant === "cinematicOverlay") {
        return (
            <div
                className="site-navigation-composition site-navigation-cinematic"
                data-navigation-anatomy="cinematic-neon-overlay"
            >
                <span aria-hidden="true" className="site-navigation-cinematic-orbit" />
                <section className="site-navigation-panel-main">
                    {commonHeading}
                    <Branches
                        activeItemUuid={activeItemUuid}
                        renderLink={renderLink}
                        variant={variant}
                    >
                        {item.children}
                    </Branches>
                </section>
                <aside className="site-navigation-cinematic-feature">
                    <FeatureCard item={item} renderLink={renderLink} variant={variant} />
                </aside>
            </div>
        );
    }

    if (variant === "technicalIndex") {
        return (
            <div className="site-navigation-composition site-navigation-technical">
                <section className="site-navigation-panel-main">
                    {commonHeading}
                    <Branches activeItemUuid={activeItemUuid} renderLink={renderLink} variant={variant}>
                        {item.children}
                    </Branches>
                </section>
                <aside className="site-navigation-technical-aside">
                    <span aria-hidden="true">INDEX / {String(item.children.length).padStart(2, "0")}</span>
                    <FeatureCard item={item} renderLink={renderLink} variant={variant} />
                </aside>
            </div>
        );
    }

    return (
        <div className={`site-navigation-composition site-navigation-composition-${variant}`}>
            <section className="site-navigation-panel-main">
                {commonHeading}
                <Branches activeItemUuid={activeItemUuid} renderLink={renderLink} variant={variant}>
                    {item.children}
                </Branches>
            </section>
            <FeatureCard item={item} renderLink={renderLink} variant={variant} />
        </div>
    );
}

function DesktopNavigation({
    activeItemUuid,
    items,
    labels,
    preservePanelOnLinkClick = false,
    previewOpenItemUuid,
    readOnly = false,
    renderLink,
    settings,
    staticPreview = false,
}: Omit<SiteNavigationProps, "className" | "mode" | "readOnly" | "renderLink" | "style"> & {
    readOnly?: boolean;
    renderLink: SiteNavigationRenderLink;
}) {
    const generatedId = useId();
    const navigationId = `site-navigation-${safeId(generatedId)}`;
    const rootRef = useRef<HTMLDivElement | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const openTimer = useRef<NavigationTimer | null>(null);
    const closeTimer = useRef<NavigationTimer | null>(null);
    const openUuidRef = useRef<string | null>(previewOpenItemUuid ?? null);
    const pinnedUuidRef = useRef<string | null>(null);
    const suppressFocusOpenUuidRef = useRef<string | null>(null);
    const [openUuid, setOpenUuidState] = useState<string | null>(previewOpenItemUuid ?? null);
    const [, setPinnedUuidState] = useState<string | null>(null);
    const openItem = items.find((item) => (
        item.uuid === openUuid && item.children.length > 0
    )) ?? null;
    const openItemUuid = openItem?.uuid ?? null;

    const setOpenUuid = useCallback((uuid: string | null) => {
        openUuidRef.current = uuid;
        setOpenUuidState(uuid);
    }, []);
    const setPinnedUuid = useCallback((uuid: string | null) => {
        pinnedUuidRef.current = uuid;
        setPinnedUuidState(uuid);
    }, []);
    const clearTimers = useCallback(() => {
        navigationClearTimeout(openTimer.current);
        navigationClearTimeout(closeTimer.current);
        openTimer.current = null;
        closeTimer.current = null;
    }, []);
    const close = useCallback((restoreFocus = false) => {
        if (readOnly) return;

        const root = rootRef.current;
        const ownerDocument = navigationOwnerDocument(root);
        const ownerWindow = ownerDocument?.defaultView;
        const previous = openUuidRef.current ?? pinnedUuidRef.current;
        clearTimers();
        setOpenUuid(null);
        setPinnedUuid(null);
        if (restoreFocus && previous && ownerDocument && ownerWindow) {
            suppressFocusOpenUuidRef.current = previous;
            ownerWindow.requestAnimationFrame(() => {
                const disclosure = Array.from(
                    root?.querySelectorAll<HTMLElement>("[data-site-navigation-trigger]") ?? [],
                ).find((candidate) => (
                    candidate.dataset.siteNavigationTrigger === previous
                ));

                disclosure?.focus();
                if (!disclosure) {
                    ownerDocument.getElementById(`${navigationId}-trigger-${safeId(previous)}`)?.focus();
                }
                ownerWindow.requestAnimationFrame(() => {
                    if (suppressFocusOpenUuidRef.current === previous) {
                        suppressFocusOpenUuidRef.current = null;
                    }
                });
            });
        }
    }, [clearTimers, navigationId, readOnly, setOpenUuid, setPinnedUuid]);
    const open = useCallback((uuid: string) => {
        if (readOnly) return;

        clearTimers();
        setOpenUuid(uuid);
    }, [clearTimers, readOnly, setOpenUuid]);
    const scheduleOpen = useCallback((uuid: string) => {
        if (readOnly || settings.openMode !== "hoverClick") return;
        navigationClearTimeout(closeTimer.current);
        navigationClearTimeout(openTimer.current);
        closeTimer.current = null;
        openTimer.current = null;
        if (openUuidRef.current === uuid) return;
        if (pinnedUuidRef.current !== null && pinnedUuidRef.current !== uuid) {
            setPinnedUuid(null);
        }
        openTimer.current = navigationSetTimeout(
            rootRef.current,
            () => {
                openTimer.current = null;
                setOpenUuid(uuid);
            },
            openUuidRef.current ? SWITCH_DELAY_MS : OPEN_DELAY_MS,
        );
    }, [readOnly, setOpenUuid, setPinnedUuid, settings.openMode]);
    const scheduleClose = useCallback(() => {
        if (readOnly || settings.openMode !== "hoverClick") return;
        navigationClearTimeout(openTimer.current);
        navigationClearTimeout(closeTimer.current);
        openTimer.current = null;
        closeTimer.current = null;
        const root = rootRef.current;
        const ownerDocument = navigationOwnerDocument(root);

        closeTimer.current = navigationSetTimeout(root, () => {
            closeTimer.current = null;
            const activeElement = ownerDocument?.activeElement ?? null;

            if (
                navigationTargetIsNode(ownerDocument, activeElement)
                && root?.contains(activeElement)
            ) {
                return;
            }
            setOpenUuid(pinnedUuidRef.current);
        }, CLOSE_DELAY_MS);
    }, [readOnly, setOpenUuid, settings.openMode]);
    const toggle = useCallback((uuid: string) => {
        if (readOnly) return;

        clearTimers();
        if (pinnedUuidRef.current === uuid) {
            setPinnedUuid(null);
            setOpenUuid(null);
        } else {
            setPinnedUuid(uuid);
            setOpenUuid(uuid);
        }
    }, [clearTimers, readOnly, setOpenUuid, setPinnedUuid]);
    const alignPanelToViewport = useCallback(() => {
        const root = rootRef.current;
        const panel = panelRef.current;
        const ownerDocument = navigationOwnerDocument(root);
        const ownerWindow = ownerDocument?.defaultView;

        if (!root || !panel || !ownerDocument || !ownerWindow) return;

        const viewportWidth = ownerDocument.documentElement.clientWidth
            || ownerWindow.innerWidth;

        if (viewportWidth <= 0) return;

        const gutter = settings.variant === "lagunaClassic"
            ? 24
            : isImageCategoryStripVariant(settings.variant) ? 0 : 16;
        const availableWidth = Math.max(0, viewportWidth - (gutter * 2));

        panel.style.setProperty(
            "--site-navigation-panel-viewport-width",
            `${availableWidth}px`,
        );

        const rootRect = root.getBoundingClientRect();
        const panelWidth = Math.min(panel.getBoundingClientRect().width, availableWidth);
        const minimumLeft = Math.min(gutter, Math.max(0, viewportWidth - panelWidth));
        const maximumLeft = Math.max(
            minimumLeft,
            viewportWidth - gutter - panelWidth,
        );
        const centredLeft = (viewportWidth - panelWidth) / 2;
        const openTrigger = openUuidRef.current
            ? Array.from(root.querySelectorAll<HTMLElement>("[data-site-navigation-trigger]"))
                .find((candidate) => (
                    candidate.dataset.siteNavigationTrigger === openUuidRef.current
                ))
            : null;
        const triggerControl = openTrigger?.closest<HTMLElement>(
            ".site-navigation-primary-parent",
        ) ?? openTrigger;
        const triggerRect = triggerControl?.getBoundingClientRect();
        const preferredLeft = isBorderedDropdownVariant(settings.variant) && triggerRect
            ? triggerRect.left + ((triggerRect.width - panelWidth) / 2)
            : settings.variant === "monochromeDropdown" && triggerRect
                ? triggerRect.left
                : centredLeft;
        const viewportLeft = Math.min(
            Math.max(preferredLeft, minimumLeft),
            maximumLeft,
        );

        panel.style.setProperty(
            "--site-navigation-panel-inline-offset",
            `${viewportLeft - rootRect.left}px`,
        );
        panel.style.setProperty(
            "--site-navigation-panel-inline-translate",
            "0px",
        );
        panel.dataset.siteNavigationPanelAlignment = "owner-viewport";
    }, [settings.variant]);

    useEffect(() => {
        if (staticPreview) return;

        const root = rootRef.current;
        const ownerDocument = navigationOwnerDocument(root);

        if (!root || !ownerDocument) return;

        const outside = (event: PointerEvent) => {
            if (
                navigationTargetIsNode(ownerDocument, event.target)
                && !root.contains(event.target)
            ) {
                close(false);
            }
        };
        ownerDocument.addEventListener("pointerdown", outside);
        return () => ownerDocument.removeEventListener("pointerdown", outside);
    }, [close, staticPreview]);
    useEffect(() => clearTimers, [clearTimers]);

    useEffect(() => {
        if (!readOnly) return;

        setOpenUuid(previewOpenItemUuid ?? null);
        setPinnedUuid(null);
    }, [previewOpenItemUuid, readOnly, setOpenUuid, setPinnedUuid]);

    useEffect(() => {
        if (!openItemUuid || staticPreview) return;

        const root = rootRef.current;
        const panel = panelRef.current;
        const ownerWindow = navigationOwnerWindow(root);

        if (!root || !panel || !ownerWindow) return;

        const frameId = ownerWindow.requestAnimationFrame(alignPanelToViewport);
        const ResizeObserverConstructor = (
            ownerWindow as Window & {
                ResizeObserver?: new (callback: ResizeObserverCallback) => ResizeObserver;
            }
        ).ResizeObserver;
        const resizeObserver = ResizeObserverConstructor
            ? new ResizeObserverConstructor(alignPanelToViewport)
            : null;

        resizeObserver?.observe(root);
        resizeObserver?.observe(panel);
        ownerWindow.addEventListener("resize", alignPanelToViewport);
        ownerWindow.addEventListener("scroll", alignPanelToViewport, true);

        return () => {
            ownerWindow.cancelAnimationFrame(frameId);
            resizeObserver?.disconnect();
            ownerWindow.removeEventListener("resize", alignPanelToViewport);
            ownerWindow.removeEventListener("scroll", alignPanelToViewport, true);
        };
    }, [alignPanelToViewport, openItemUuid, staticPreview]);

    const focusTrigger = (item: SiteNavigationItem | undefined) => {
        const owner = navigationOwnerDocument(rootRef.current)?.getElementById(
            `${navigationId}-trigger-${safeId(item?.uuid ?? "")}`,
        );
        const target = owner?.matches("a[href],button")
            ? owner
            : owner?.querySelector<HTMLElement>("a[href],button");

        target?.focus();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>, item: SiteNavigationItem, index: number) => {
        if (event.defaultPrevented) return;

        const action = siteNavigationKeyboardAction(
            event.key,
            index,
            items.length,
            item.children.length > 0,
        );

        if (action?.type === "close") {
            event.preventDefault();
            close(true);
            return;
        }
        if (action?.type === "openPanel") {
            event.preventDefault();
            open(item.uuid);
            const ownerDocument = navigationOwnerDocument(rootRef.current);
            const ownerWindow = ownerDocument?.defaultView;

            if (!ownerDocument || !ownerWindow) return;

            ownerWindow.requestAnimationFrame(() => {
                ownerDocument.getElementById(
                    `${navigationId}-panel-${safeId(item.uuid)}`,
                )?.querySelector<HTMLElement>("a[href],button")?.focus();
            });
            return;
        }
        if (action?.type === "focus") {
            event.preventDefault();
            focusTrigger(items[action.index]);
        }
    };

    return (
        <div
            className="site-navigation-desktop-shell"
            data-site-navigation-desktop-shell="true"
            onBlurCapture={(event) => {
                const ownerDocument = navigationOwnerDocument(event.currentTarget);

                if (
                    navigationTargetIsNode(ownerDocument, event.relatedTarget)
                    && event.currentTarget.contains(event.relatedTarget)
                ) {
                    return;
                }

                close(false);
            }}
            onClickCapture={(event) => {
                const ownerDocument = navigationOwnerDocument(event.currentTarget);

                if (
                    !preservePanelOnLinkClick
                    && navigationTargetIsElement(ownerDocument, event.target)
                    && event.target.closest("a[href]")
                ) {
                    close(false);
                }
            }}
            onKeyDownCapture={(event) => {
                if (event.key === "Escape" && openUuidRef.current !== null) {
                    event.preventDefault();
                    close(true);
                }
            }}
            ref={rootRef}
        >
            <ul
                className={`site-navigation-primary site-navigation-primary-${settings.variant} site-navigation-align-${settings.alignment}`}
                onMouseLeave={scheduleClose}
            >
                {items.map((item, index) => {
                    const hasChildren = item.children.length > 0;
                    const hasEndpoint = itemHref(item) !== null;
                    const expanded = hasChildren && openUuid === item.uuid;
                    const active = itemContainsUuid(item, activeItemUuid);
                    const triggerId = `${navigationId}-trigger-${safeId(item.uuid)}`;
                    const panelId = `${navigationId}-panel-${safeId(item.uuid)}`;
                    const primaryContent = (
                        <>
                            {settings.variant !== "lagunaClassic"
                            && !isCompactTextNavigationVariant(settings.variant)
                            && !isImageCategoryStripVariant(settings.variant)
                            && !isCleanNavigationVariant(settings.variant) ? (
                                <span
                                    aria-hidden="true"
                                    className="site-navigation-primary-marker"
                                    data-site-navigation-primary-marker={item.uuid}
                                >
                                    <Marker index={index} item={item} variant={settings.variant} />
                                </span>
                            ) : null}
                            <span className="site-navigation-primary-label">{item.label}</span>
                        </>
                    );

                    return (
                        <li
                            className={classes(
                                "site-navigation-primary-item",
                                active && "is-active",
                                expanded && "is-open",
                                settings.variant === "lagunaClassic"
                                    && siteNavigationLagunaClassicSaleItem(item)
                                    && "is-sale",
                            )}
                            key={item.uuid}
                            onFocusCapture={() => {
                                if (!hasChildren) return;

                                if (suppressFocusOpenUuidRef.current === item.uuid) {
                                    suppressFocusOpenUuidRef.current = null;
                                    return;
                                }

                                open(item.uuid);
                            }}
                            onMouseEnter={() => hasChildren && scheduleOpen(item.uuid)}
                            onKeyDown={(event) => handleKeyDown(event, item, index)}
                        >
                            {hasChildren && hasEndpoint ? (
                                <div className="site-navigation-primary-parent">
                                    {renderNavigationItemLink(renderLink, {
                                        ariaCurrent: item.uuid === activeItemUuid ? "page" : undefined,
                                        children: primaryContent,
                                        className: "site-navigation-primary-link site-navigation-primary-parent-link",
                                        id: triggerId,
                                        item,
                                    })}
                                    <button
                                        aria-controls={panelId}
                                        aria-expanded={expanded}
                                        aria-label={submenuToggleLabel(labels, item, expanded)}
                                        className="site-navigation-primary-toggle"
                                        data-site-navigation-item-uuid={item.uuid}
                                        data-site-navigation-trigger={item.uuid}
                                        onClick={() => toggle(item.uuid)}
                                        type="button"
                                    >
                                        <span aria-hidden="true" className="site-navigation-trigger-chevron" />
                                    </button>
                                </div>
                            ) : hasChildren ? (
                                <button
                                    aria-controls={panelId}
                                    aria-expanded={expanded}
                                    className="site-navigation-primary-trigger"
                                    data-site-navigation-item-uuid={item.uuid}
                                    data-site-navigation-trigger={item.uuid}
                                    id={triggerId}
                                    onClick={() => toggle(item.uuid)}
                                    type="button"
                                >
                                    {primaryContent}
                                    <span aria-hidden="true" className="site-navigation-trigger-chevron" />
                                </button>
                            ) : renderNavigationItemLink(renderLink, {
                                ariaCurrent: item.uuid === activeItemUuid ? "page" : undefined,
                                children: primaryContent,
                                className: "site-navigation-primary-link",
                                id: triggerId,
                                item,
                            })}
                        </li>
                    );
                })}
            </ul>
            {openItem ? (
                <div
                    aria-hidden={false}
                    aria-labelledby={`${navigationId}-trigger-${safeId(openItem.uuid)}`}
                    className={`site-navigation-panel-wrap site-navigation-panel-wrap-${settings.variant} is-open`}
                    data-site-navigation-panel={openItem.uuid}
                    data-site-navigation-panel-alignment={staticPreview
                        ? "local-preview"
                        : undefined}
                    id={`${navigationId}-panel-${safeId(openItem.uuid)}`}
                    onMouseEnter={() => {
                        navigationClearTimeout(closeTimer.current);
                        closeTimer.current = null;
                    }}
                    onMouseLeave={scheduleClose}
                    ref={panelRef}
                    role="region"
                    style={staticPreview ? {
                        "--site-navigation-panel-inline-offset": "50%",
                        "--site-navigation-panel-inline-translate": "-50%",
                        "--site-navigation-panel-viewport-width": "var(--site-container, 500px)",
                    } as CSSProperties : undefined}
                >
                    <div className={`site-navigation-panel site-navigation-panel-${settings.variant}`}>
                        <PanelComposition
                            activeItemUuid={activeItemUuid}
                            item={openItem}
                            labels={labels}
                            renderLink={renderLink}
                            variant={settings.variant}
                        />
                    </div>
                </div>
            ) : null}
            {openUuid ? <button aria-label={labels.navigation} className="site-navigation-scrim" onClick={() => close(false)} tabIndex={-1} type="button" /> : null}
        </div>
    );
}

function LagunaClassicDesktopNavigation({
    activeItemUuid,
    items,
    labels,
    preservePanelOnLinkClick = false,
    previewOpenItemUuid,
    readOnly = false,
    renderLink,
    settings,
    staticPreview = false,
}: Omit<SiteNavigationProps, "className" | "mode" | "readOnly" | "renderLink" | "style"> & {
    readOnly?: boolean;
    renderLink: SiteNavigationRenderLink;
}) {
    const generatedId = useId();
    const navigationId = `site-navigation-laguna-classic-${safeId(generatedId)}`;
    const rootRef = useRef<HTMLDivElement | null>(null);
    const initialRoot = siteNavigationLagunaClassicRoot(
        items,
        previewOpenItemUuid ?? activeItemUuid,
    );
    const [selectedRootUuid, setSelectedRootUuid] = useState<string | null>(
        initialRoot?.uuid ?? null,
    );
    const selectedRoot = selectedRootUuid === null
        ? null
        : items.find((item) => item.uuid === selectedRootUuid) ?? initialRoot;
    const previewPanelUuid = previewOpenItemUuid
        ? selectedRoot?.children.find((item) => (
            item.uuid === previewOpenItemUuid && item.children.length > 0
        ))?.uuid
            ?? selectedRoot?.children.find((item) => item.children.length > 0)?.uuid
        : undefined;

    useEffect(() => {
        const activeRoot = siteNavigationLagunaClassicRoot(items, activeItemUuid);

        if (activeRoot && activeItemUuid && itemContainsUuid(activeRoot, activeItemUuid)) {
            setSelectedRootUuid(activeRoot.uuid);
        }
    }, [activeItemUuid, items]);

    const selectRoot = useCallback((item: SiteNavigationItem) => {
        if (readOnly || item.children.length === 0) return;

        setSelectedRootUuid(item.uuid);
    }, [readOnly]);
    const toggleRoot = useCallback((item: SiteNavigationItem) => {
        if (readOnly || item.children.length === 0) return;

        setSelectedRootUuid((current) => current === item.uuid ? null : item.uuid);
    }, [readOnly]);
    const focusRoot = (item: SiteNavigationItem | undefined) => {
        navigationOwnerDocument(rootRef.current)?.getElementById(
            `${navigationId}-trigger-${safeId(item?.uuid ?? "")}`,
        )?.focus();
    };
    const handleRootKeyDown = (
        event: KeyboardEvent<HTMLElement>,
        item: SiteNavigationItem,
        index: number,
    ) => {
        if (event.defaultPrevented) return;

        if (event.key === "ArrowDown" && item.children.length > 0) {
            event.preventDefault();
            selectRoot(item);
            const ownerDocument = navigationOwnerDocument(rootRef.current);
            const ownerWindow = ownerDocument?.defaultView;

            if (!ownerDocument || !ownerWindow) return;

            ownerWindow.requestAnimationFrame(() => {
                ownerDocument.getElementById(
                    `${navigationId}-panel-${safeId(item.uuid)}`,
                )?.querySelector<HTMLElement>("a[href],button")?.focus();
            });
            return;
        }

        const action = siteNavigationKeyboardAction(
            event.key,
            index,
            items.length,
            false,
        );

        if (action?.type === "focus") {
            event.preventDefault();
            focusRoot(items[action.index]);
        }
    };

    return (
        <div
            className="site-navigation-laguna-classic-shell"
            data-navigation-anatomy="laguna-classic-two-tier"
            data-site-navigation-classic-root={selectedRoot?.uuid}
            ref={rootRef}
        >
            <ul
                className={`site-navigation-laguna-classic-roots site-navigation-align-${settings.alignment}`}
                data-navigation-tier="section"
            >
                {items.map((item, index) => {
                    const active = itemContainsUuid(item, activeItemUuid);
                    const selected = item.uuid === selectedRoot?.uuid;
                    const hasChildren = item.children.length > 0;
                    const hasEndpoint = itemHref(item) !== null;
                    const panelId = `${navigationId}-panel-${safeId(item.uuid)}`;
                    const triggerId = `${navigationId}-trigger-${safeId(item.uuid)}`;

                    return (
                        <li
                            className={classes(
                                "site-navigation-laguna-classic-root-item",
                                active && "is-active",
                                selected && "is-selected",
                                siteNavigationLagunaClassicSaleItem(item) && "is-sale",
                            )}
                            key={item.uuid}
                            onKeyDown={(event) => handleRootKeyDown(event, item, index)}
                            onMouseEnter={() => {
                                if (settings.openMode === "hoverClick") selectRoot(item);
                            }}
                        >
                            {hasChildren && hasEndpoint ? (
                                <div className="site-navigation-laguna-classic-root-control">
                                    {renderNavigationItemLink(renderLink, {
                                        ariaCurrent: item.uuid === activeItemUuid ? "page" : undefined,
                                        children: item.label,
                                        className: "site-navigation-laguna-classic-root-link",
                                        id: triggerId,
                                        item,
                                    })}
                                    <button
                                        aria-controls={panelId}
                                        aria-expanded={selected}
                                        aria-label={submenuToggleLabel(labels, item, selected)}
                                        className="site-navigation-laguna-classic-root-toggle"
                                        data-site-navigation-item-uuid={item.uuid}
                                        data-site-navigation-trigger={item.uuid}
                                        onClick={() => toggleRoot(item)}
                                        type="button"
                                    >
                                        <span aria-hidden="true" className="site-navigation-trigger-chevron" />
                                    </button>
                                </div>
                            ) : hasChildren ? (
                                <button
                                    aria-controls={panelId}
                                    aria-expanded={selected}
                                    className="site-navigation-laguna-classic-root-link"
                                    data-site-navigation-item-uuid={item.uuid}
                                    data-site-navigation-trigger={item.uuid}
                                    id={triggerId}
                                    onClick={() => toggleRoot(item)}
                                    type="button"
                                >
                                    {item.label}
                                </button>
                            ) : renderNavigationItemLink(renderLink, {
                                ariaCurrent: item.uuid === activeItemUuid ? "page" : undefined,
                                children: item.label,
                                className: "site-navigation-laguna-classic-root-link",
                                id: triggerId,
                                item,
                            })}
                        </li>
                    );
                })}
            </ul>
            {selectedRoot && selectedRoot.children.length > 0 ? (
                <div
                    aria-labelledby={`${navigationId}-trigger-${safeId(selectedRoot.uuid)}`}
                    className="site-navigation-laguna-classic-sub-row"
                    data-navigation-tier="category"
                    data-site-navigation-panel={selectedRoot.uuid}
                    data-site-navigation-classic-sub-row={selectedRoot.uuid}
                    id={`${navigationId}-panel-${safeId(selectedRoot.uuid)}`}
                    role="region"
                >
                    {settings.openMode === "click" && itemHref(selectedRoot) !== null ? (
                        <div
                            className="site-navigation-laguna-classic-root-target-wrap"
                            data-site-navigation-classic-root-target={selectedRoot.uuid}
                        >
                            {renderNavigationItemLink(renderLink, {
                                ariaCurrent: selectedRoot.uuid === activeItemUuid ? "page" : undefined,
                                children: (
                                    <>
                                        <span>{labels.showAll}</span>
                                        <span
                                            aria-hidden="true"
                                            className="site-navigation-laguna-classic-inline-chevron"
                                        />
                                    </>
                                ),
                                className: "site-navigation-laguna-classic-root-target",
                                item: selectedRoot,
                            })}
                        </div>
                    ) : null}
                    <DesktopNavigation
                        activeItemUuid={activeItemUuid}
                        items={selectedRoot.children}
                        key={selectedRoot.uuid}
                        labels={labels}
                        preservePanelOnLinkClick={preservePanelOnLinkClick}
                        previewOpenItemUuid={previewPanelUuid}
                        readOnly={readOnly}
                        renderLink={renderLink}
                        settings={settings}
                        staticPreview={staticPreview}
                    />
                </div>
            ) : null}
        </div>
    );
}

function MobileBranch({
    activeItemUuid,
    depth,
    index,
    item,
    labels,
    navigationId,
    openUuids,
    renderLink,
    toggle,
    variant,
}: {
    activeItemUuid?: string;
    depth: number;
    index: number;
    item: SiteNavigationItem;
    labels: SiteNavigationLabels;
    navigationId: string;
    openUuids: ReadonlySet<string>;
    renderLink: SiteNavigationRenderLink;
    toggle: (uuid: string) => void;
    variant: SiteNavigationVariant;
}) {
    const hasChildren = item.children.length > 0;
    const hasEndpoint = itemHref(item) !== null;
    const expanded = hasChildren && openUuids.has(item.uuid);
    const id = `${navigationId}-mobile-${safeId(item.uuid)}`;
    const active = itemContainsUuid(item, activeItemUuid);

    return (
        <li className={classes("site-navigation-mobile-item", active && "is-active")} data-navigation-depth={depth}>
            {hasChildren && hasEndpoint ? (
                <div className="site-navigation-mobile-parent">
                    {renderNavigationItemLink(renderLink, {
                        ariaCurrent: item.uuid === activeItemUuid ? "page" : undefined,
                        children: <><Marker index={index} item={item} variant={variant} /><span className="site-navigation-mobile-label">{item.label}</span></>,
                        className: "site-navigation-mobile-link site-navigation-mobile-parent-link",
                        id: `${id}-trigger`,
                        item,
                    })}
                    <button
                        aria-controls={`${id}-panel`}
                        aria-expanded={expanded}
                        aria-label={submenuToggleLabel(labels, item, expanded)}
                        className="site-navigation-mobile-toggle"
                        data-site-navigation-item-uuid={item.uuid}
                        data-site-navigation-mobile-toggle={item.uuid}
                        onClick={() => toggle(item.uuid)}
                        type="button"
                    >
                        <span aria-hidden="true" className="site-navigation-mobile-chevron" />
                    </button>
                </div>
            ) : hasChildren ? (
                <button
                    aria-controls={`${id}-panel`}
                    aria-expanded={expanded}
                    className="site-navigation-mobile-trigger"
                    data-site-navigation-item-uuid={item.uuid}
                    data-site-navigation-mobile-toggle={item.uuid}
                    id={`${id}-trigger`}
                    onClick={() => toggle(item.uuid)}
                    type="button"
                >
                    <Marker index={index} item={item} variant={variant} />
                    <span className="site-navigation-mobile-label">{item.label}</span>
                    <span aria-hidden="true" className="site-navigation-mobile-chevron" />
                </button>
            ) : renderNavigationItemLink(renderLink, {
                ariaCurrent: item.uuid === activeItemUuid ? "page" : undefined,
                children: <><Marker index={index} item={item} variant={variant} /><span className="site-navigation-mobile-label">{item.label}</span></>,
                className: "site-navigation-mobile-link",
                item,
            })}
            {hasChildren ? (
                <div
                    aria-labelledby={`${id}-trigger`}
                    className="site-navigation-mobile-panel"
                    data-site-navigation-mobile-panel={item.uuid}
                    hidden={!expanded}
                    id={`${id}-panel`}
                    role="region"
                >
                    {itemHref(item) !== null ? renderNavigationItemLink(renderLink, {
                        children: <><span className="site-navigation-mobile-label">{labels.showAll}</span><span aria-hidden="true" className="site-navigation-mobile-arrow"> →</span></>,
                        className: "site-navigation-mobile-show-all",
                        item,
                    }) : null}
                    <ul>
                        {item.children.map((child, childIndex) => (
                            <MobileBranch
                                activeItemUuid={activeItemUuid}
                                depth={depth + 1}
                                index={childIndex}
                                item={child}
                                key={child.uuid}
                                labels={labels}
                                navigationId={navigationId}
                                openUuids={openUuids}
                                renderLink={renderLink}
                                toggle={toggle}
                                variant={variant}
                            />
                        ))}
                    </ul>
                </div>
            ) : null}
        </li>
    );
}

function LagunaClassicMobileCategory({
    activeItemUuid,
    item,
    renderLink,
}: {
    activeItemUuid?: string;
    item: SiteNavigationItem;
    renderLink: SiteNavigationRenderLink;
}) {
    return (
        <div className="site-navigation-laguna-classic-mobile-category" data-navigation-depth={1}>
            {renderNavigationItemLink(renderLink, {
                ariaCurrent: item.uuid === activeItemUuid ? "page" : undefined,
                children: (
                    <>
                        <span>{item.label}</span>
                        <span
                            aria-hidden="true"
                            className="site-navigation-laguna-classic-mobile-inline-chevron"
                        />
                    </>
                ),
                className: classes(
                    "site-navigation-laguna-classic-mobile-category-link",
                    item.children.length > 0 && "has-children",
                    siteNavigationLagunaClassicSaleItem(item) && "is-sale",
                ),
                item,
            })}
            {item.children.length > 0 ? (
                <div className="site-navigation-laguna-classic-mobile-children">
                    {item.children.map((child) => (
                        <Fragment key={child.uuid}>
                            {renderNavigationItemLink(renderLink, {
                                ariaCurrent: child.uuid === activeItemUuid ? "page" : undefined,
                                children: child.label,
                                className: classes(
                                    "site-navigation-laguna-classic-mobile-child-link",
                                    itemContainsUuid(child, activeItemUuid) && "is-active",
                                    siteNavigationLagunaClassicSaleItem(child) && "is-sale",
                                ),
                                item: child,
                            })}
                        </Fragment>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function LagunaClassicMobileNavigation({
    activeItemUuid,
    items,
    labels,
    previewOpenItemUuid,
    readOnly = false,
    renderLink,
}: Omit<SiteNavigationProps, "className" | "mode" | "readOnly" | "renderLink" | "settings" | "style"> & {
    readOnly?: boolean;
    renderLink: SiteNavigationRenderLink;
}) {
    const generatedId = useId();
    const navigationId = `site-navigation-laguna-classic-mobile-${safeId(generatedId)}`;
    const rootRef = useRef<HTMLDivElement | null>(null);
    const tabItems = items.slice(0, 5);
    const initialRoot = siteNavigationLagunaClassicRoot(
        tabItems,
        previewOpenItemUuid ?? activeItemUuid,
    );
    const [selectedRootUuid, setSelectedRootUuid] = useState<string | null>(
        initialRoot?.uuid ?? null,
    );
    const selectedRoot = selectedRootUuid === null
        ? null
        : items.find((item) => item.uuid === selectedRootUuid) ?? initialRoot;
    const toggleRoot = useCallback((uuid: string) => {
        if (readOnly) return;

        setSelectedRootUuid((current) => current === uuid ? null : uuid);
    }, [readOnly]);

    useEffect(() => {
        const activeRoot = siteNavigationLagunaClassicRoot(items.slice(0, 5), activeItemUuid);

        if (activeRoot && activeItemUuid && itemContainsUuid(activeRoot, activeItemUuid)) {
            setSelectedRootUuid(activeRoot.uuid);
        }
    }, [activeItemUuid, items]);

    const handleTabKeyDown = (event: KeyboardEvent<HTMLElement>, index: number) => {
        const action = siteNavigationKeyboardAction(
            event.key,
            index,
            tabItems.length,
            false,
        );

        if (action?.type !== "focus") return;

        const target = tabItems[action.index];

        if (!target) return;

        event.preventDefault();
        setSelectedRootUuid(target.uuid);
        const ownerDocument = navigationOwnerDocument(rootRef.current);
        const ownerWindow = ownerDocument?.defaultView;

        if (!ownerDocument || !ownerWindow) return;

        ownerWindow.requestAnimationFrame(() => {
            ownerDocument.getElementById(
                `${navigationId}-tab-${safeId(target.uuid)}`,
            )?.focus();
        });
    };

    return (
        <div
            className="site-navigation-laguna-classic-mobile"
            data-navigation-anatomy="laguna-classic-mobile-tabs"
            ref={rootRef}
        >
            <ul
                aria-label={labels.navigation}
                className="site-navigation-laguna-classic-tabs"
            >
                {tabItems.map((item, index) => {
                    const selected = item.uuid === selectedRoot?.uuid;
                    const hasChildren = item.children.length > 0;
                    const hasEndpoint = itemHref(item) !== null;
                    const triggerId = `${navigationId}-tab-${safeId(item.uuid)}`;

                    return (
                        <li
                            className={classes(
                                "site-navigation-laguna-classic-tab-item",
                                selected && "is-selected",
                            )}
                            key={item.uuid}
                            onKeyDown={(event) => handleTabKeyDown(event, index)}
                        >
                            {hasChildren && hasEndpoint ? (
                                <div className="site-navigation-laguna-classic-tab-control">
                                    {renderNavigationItemLink(renderLink, {
                                        ariaCurrent: item.uuid === activeItemUuid ? "page" : undefined,
                                        children: item.label,
                                        className: "site-navigation-laguna-classic-tab site-navigation-laguna-classic-tab-link",
                                        id: triggerId,
                                        item,
                                    })}
                                    <button
                                        aria-controls={`${navigationId}-panel`}
                                        aria-expanded={selected}
                                        aria-label={submenuToggleLabel(labels, item, selected)}
                                        className="site-navigation-laguna-classic-tab-toggle"
                                        data-site-navigation-item-uuid={item.uuid}
                                        data-site-navigation-mobile-toggle={item.uuid}
                                        disabled={readOnly}
                                        onClick={() => toggleRoot(item.uuid)}
                                        type="button"
                                    >
                                        <span aria-hidden="true" className="site-navigation-mobile-chevron" />
                                    </button>
                                </div>
                            ) : hasChildren ? (
                                <button
                                    aria-controls={`${navigationId}-panel`}
                                    aria-expanded={selected}
                                    className="site-navigation-laguna-classic-tab"
                                    data-site-navigation-item-uuid={item.uuid}
                                    data-site-navigation-mobile-toggle={item.uuid}
                                    disabled={readOnly}
                                    id={triggerId}
                                    onClick={() => toggleRoot(item.uuid)}
                                    type="button"
                                >
                                    {item.label}
                                </button>
                            ) : renderNavigationItemLink(renderLink, {
                                ariaCurrent: item.uuid === activeItemUuid ? "page" : undefined,
                                children: item.label,
                                className: "site-navigation-laguna-classic-tab site-navigation-laguna-classic-tab-link",
                                id: triggerId,
                                item,
                            })}
                        </li>
                    );
                })}
            </ul>
            {selectedRoot ? (
                <div
                    aria-labelledby={`${navigationId}-tab-${safeId(selectedRoot.uuid)}`}
                    className="site-navigation-laguna-classic-mobile-panel"
                    id={`${navigationId}-panel`}
                    role="region"
                >
                    {renderNavigationItemLink(renderLink, {
                        ariaCurrent: selectedRoot.uuid === activeItemUuid ? "page" : undefined,
                        children: (
                            <>
                                <span>{selectedRoot.label}</span>
                                <span
                                    aria-hidden="true"
                                    className="site-navigation-laguna-classic-mobile-inline-chevron"
                                />
                            </>
                        ),
                        className: classes(
                            "site-navigation-laguna-classic-mobile-root-link",
                            siteNavigationLagunaClassicSaleItem(selectedRoot) && "is-sale",
                        ),
                        item: selectedRoot,
                    })}
                    <div className="site-navigation-laguna-classic-mobile-categories">
                        {selectedRoot.children.map((item) => (
                            <LagunaClassicMobileCategory
                                activeItemUuid={activeItemUuid}
                                item={item}
                                key={item.uuid}
                                renderLink={renderLink}
                            />
                        ))}
                    </div>
                    {items.length > 5 ? (
                        <div className="site-navigation-laguna-classic-mobile-more">
                            {items.slice(5).map((item) => (
                                <Fragment key={item.uuid}>
                                    {renderNavigationItemLink(renderLink, {
                                        ariaCurrent: item.uuid === activeItemUuid ? "page" : undefined,
                                        children: item.label,
                                        className: classes(
                                            "site-navigation-laguna-classic-mobile-more-link",
                                            siteNavigationLagunaClassicSaleItem(item) && "is-sale",
                                        ),
                                        item,
                                    })}
                                </Fragment>
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

function MobileNavigation({
    activeItemUuid,
    items,
    labels,
    previewOpenItemUuid,
    readOnly = false,
    renderLink,
    settings,
}: Omit<SiteNavigationProps, "className" | "mode" | "readOnly" | "renderLink" | "style"> & {
    readOnly?: boolean;
    renderLink: SiteNavigationRenderLink;
}) {
    const generatedId = useId();
    const navigationId = `site-navigation-${safeId(generatedId)}`;
    const [openUuids, setOpenUuids] = useState<Set<string>>(() => {
        return new Set(itemOpenPath(items, previewOpenItemUuid ?? activeItemUuid));
    });
    const toggle = useCallback((uuid: string) => {
        if (readOnly) return;

        setOpenUuids((current) => {
            const next = new Set(current);
            if (next.has(uuid)) next.delete(uuid);
            else next.add(uuid);
            return next;
        });
    }, [readOnly]);

    useEffect(() => {
        const openPath = itemOpenPath(items, previewOpenItemUuid ?? activeItemUuid);

        if (openPath.length === 0) return;

        setOpenUuids((current) => {
            const next = new Set(current);
            openPath.forEach((uuid) => next.add(uuid));

            return next;
        });
    }, [activeItemUuid, items, previewOpenItemUuid]);

    const tree = (
        <ul
            className={`site-navigation-mobile-tree site-navigation-mobile-${settings.variant}`}
            data-navigation-mobile-signature={neutralNavigationSignature(settings.variant)}
            data-site-navigation-mobile-tree="true"
        >
            {items.map((item, index) => (
                <MobileBranch
                    activeItemUuid={activeItemUuid}
                    depth={0}
                    index={index}
                    item={item}
                    key={item.uuid}
                    labels={labels}
                    navigationId={navigationId}
                    openUuids={openUuids}
                    renderLink={renderLink}
                    toggle={toggle}
                    variant={settings.variant}
                />
            ))}
        </ul>
    );

    if (isBorderedDropdownVariant(settings.variant)) {
        return (
            <div
                className="site-navigation-mobile-experience site-navigation-mobile-experience-bordered"
                data-navigation-mobile-anatomy="pure-white-text-accordion"
            >
                {tree}
            </div>
        );
    }

    if (isImageCategoryStripVariant(settings.variant)) {
        return (
            <div
                className="site-navigation-mobile-experience site-navigation-mobile-experience-image-strip"
                data-navigation-mobile-anatomy="outlined-category-accordion"
            >
                {tree}
            </div>
        );
    }

    if (settings.variant === "monochromeDropdown") {
        return (
            <div
                className="site-navigation-mobile-experience site-navigation-mobile-experience-monochrome"
                data-navigation-mobile-anatomy="monochrome-text-accordion"
            >
                {tree}
            </div>
        );
    }

    return tree;
}

const NAVIGATION_CSS = `
.site-navigation-root{position:relative;z-index:45;color:var(--site-navigation-text);font-family:var(--site-body-font,inherit)}
.site-navigation-root *{box-sizing:border-box}.site-navigation-root ul{list-style:none;margin:0;padding:0}.site-navigation-root a{text-decoration:none;color:inherit}
.site-navigation-desktop-shell{position:relative}
.site-navigation-primary{display:flex;min-width:0;align-items:center;gap:clamp(.2rem,.8vw,.85rem)}.site-navigation-align-start{justify-content:flex-start}.site-navigation-align-center{justify-content:center}.site-navigation-align-end{justify-content:flex-end}.site-navigation-align-stretch>li{flex:1}.site-navigation-primary-item{position:relative;min-width:0}.site-navigation-primary-trigger,.site-navigation-primary-link{display:flex;min-height:42px;align-items:center;justify-content:center;gap:.45rem;border:0;border-radius:var(--site-navigation-radius);background:transparent;padding:.55rem .75rem;color:inherit;font:inherit;font-size:.82rem;font-weight:750;white-space:nowrap;cursor:pointer;transition:background .18s,color .18s,transform .18s}.site-navigation-primary-trigger:hover,.site-navigation-primary-link:hover,.site-navigation-primary-item.is-active>.site-navigation-primary-trigger,.site-navigation-primary-item.is-active>.site-navigation-primary-link{background:color-mix(in srgb,var(--site-navigation-accent) 10%,transparent);color:var(--site-navigation-accent)}.site-navigation-primary-trigger:focus-visible,.site-navigation-primary-link:focus-visible,.site-navigation-root a:focus-visible,.site-navigation-root button:focus-visible{outline:2px solid var(--site-navigation-accent);outline-offset:2px}.site-navigation-primary-marker{display:inline-flex;flex:none;align-items:center}.site-navigation-primary-label{min-width:0;overflow:hidden;text-overflow:ellipsis}.site-navigation-primary-marker .site-navigation-marker-code,.site-navigation-primary-marker .site-navigation-marker-room,.site-navigation-primary-marker .site-navigation-marker-step{width:1.4rem;height:1.4rem}.site-navigation-primary-marker .site-navigation-marker-index{min-width:1.4rem}.site-navigation-trigger-chevron{position:relative;display:block;width:.75rem;height:.75rem;flex:none;transition:transform .18s}.site-navigation-trigger-chevron::before{position:absolute;top:50%;left:50%;width:.375rem;height:.375rem;border-right:1.5px solid currentColor;border-bottom:1.5px solid currentColor;content:"";transform:translate(-50%,-68%) rotate(45deg)}.is-open>.site-navigation-primary-trigger .site-navigation-trigger-chevron{transform:rotate(180deg)}
.site-navigation-primary-parent{display:flex;min-width:0;align-items:stretch;border-radius:var(--site-navigation-radius)}.site-navigation-primary-parent-link{min-width:0;flex:1;padding-right:.35rem}.site-navigation-primary-toggle{display:grid;min-height:42px;width:2rem;flex:none;place-items:center;border:0;border-radius:0 var(--site-navigation-radius) var(--site-navigation-radius) 0;background:transparent;padding:.55rem .45rem;color:inherit;cursor:pointer;transition:background .18s,color .18s}.site-navigation-primary-parent:hover,.site-navigation-primary-item.is-active>.site-navigation-primary-parent,.site-navigation-primary-item.is-open>.site-navigation-primary-parent{background:color-mix(in srgb,var(--site-navigation-accent) 10%,transparent);color:var(--site-navigation-accent)}.site-navigation-primary-parent:hover>.site-navigation-primary-link,.site-navigation-primary-item.is-active>.site-navigation-primary-parent>.site-navigation-primary-link,.site-navigation-primary-item.is-open>.site-navigation-primary-parent>.site-navigation-primary-link{background:transparent;color:inherit}.site-navigation-primary-item.is-open>.site-navigation-primary-parent .site-navigation-trigger-chevron{transform:rotate(180deg)}
.site-navigation-primary-audienceMega .site-navigation-primary-trigger,.site-navigation-primary-audienceMega .site-navigation-primary-link{border-radius:999px;padding-inline:.85rem}.site-navigation-primary-editorialOverlay .site-navigation-primary-trigger,.site-navigation-primary-editorialOverlay .site-navigation-primary-link{border-radius:0;font-family:var(--site-heading-font,inherit);font-weight:650;letter-spacing:.015em}.site-navigation-primary-categoryPills .site-navigation-primary-trigger,.site-navigation-primary-categoryPills .site-navigation-primary-link{border:1px solid var(--site-navigation-border);border-radius:999px;background:color-mix(in srgb,var(--site-navigation-surface) 55%,transparent)}.site-navigation-primary-routineMega .site-navigation-primary-trigger,.site-navigation-primary-routineMega .site-navigation-primary-link{border-radius:999px;background:color-mix(in srgb,var(--site-navigation-accent) 6%,transparent)}.site-navigation-primary-technicalIndex .site-navigation-primary-trigger,.site-navigation-primary-technicalIndex .site-navigation-primary-link{border-bottom:1px solid var(--site-navigation-border);border-radius:0;font-family:ui-monospace,SFMono-Regular,monospace;font-size:.75rem;letter-spacing:.06em;text-transform:uppercase}
.site-navigation-panel-wrap{visibility:hidden;position:absolute;top:calc(100% + .35rem);left:var(--site-navigation-panel-inline-offset,50%);width:min(var(--site-container,1200px),var(--site-navigation-panel-viewport-width,calc(100vw - 2rem)));transform:translate(var(--site-navigation-panel-inline-translate,-50%),-.35rem);opacity:0;pointer-events:none;transition:opacity .16s,transform .16s,visibility 0s linear .16s}.site-navigation-panel-wrap.is-open{visibility:visible;transform:translate(var(--site-navigation-panel-inline-translate,-50%),0);opacity:1;pointer-events:auto;transition-delay:0s}.site-navigation-panel{max-height:min(72vh,45rem);overflow:auto;border:1px solid var(--site-navigation-border);border-radius:var(--site-navigation-radius);background:var(--site-navigation-background);box-shadow:var(--site-navigation-shadow);color:var(--site-navigation-text)}.site-navigation-scrim{position:fixed;z-index:-1;inset:0;border:0;background:color-mix(in srgb,var(--site-navigation-text) 12%,transparent)}
.site-navigation-composition{display:grid;grid-template-columns:minmax(0,1fr) minmax(13rem,22%)}.site-navigation-panel-main{min-width:0;padding:clamp(1.25rem,2.5vw,2.35rem)}.site-navigation-panel-heading{display:flex;align-items:flex-end;justify-content:space-between;gap:1rem;margin-bottom:1.35rem;border-bottom:1px solid var(--site-navigation-border);padding-bottom:1rem}.site-navigation-panel-heading>div{display:grid;gap:.3rem}.site-navigation-panel-heading strong{font-family:var(--site-heading-font,inherit);font-size:clamp(1.15rem,2vw,1.8rem);line-height:1.05}.site-navigation-panel-eyebrow{font-size:.65rem;font-weight:850;letter-spacing:.18em;color:var(--site-navigation-muted)}.site-navigation-show-all{flex:none;font-size:.75rem;font-weight:800;color:var(--site-navigation-accent)}
.site-navigation-tree{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:1rem 1.4rem}.site-navigation-branch{min-width:0}.site-navigation-branch-link{display:flex;min-height:2rem;align-items:center;gap:.55rem;border-radius:var(--site-navigation-radius);padding:.3rem;color:inherit;font-size:.83rem;font-weight:780}.site-navigation-branch-link:hover,.site-navigation-branch-link.is-active{background:color-mix(in srgb,var(--site-navigation-accent) 9%,transparent);color:var(--site-navigation-accent)}.site-navigation-branch-label{min-width:0;overflow:hidden;text-overflow:ellipsis}.site-navigation-branch-chevron{position:relative;width:.7rem;height:.7rem;flex:none;margin-left:auto;color:var(--site-navigation-muted)}.site-navigation-branch-chevron::before{position:absolute;top:50%;left:45%;width:.35rem;height:.35rem;border-right:1.5px solid currentColor;border-bottom:1.5px solid currentColor;content:"";transform:translate(-50%,-50%) rotate(-45deg)}.site-navigation-children{display:grid;gap:.15rem;margin:.35rem 0 0 .55rem!important;border-left:1px solid var(--site-navigation-border);padding-left:.6rem!important}.site-navigation-depth-1>.site-navigation-branch-link,.site-navigation-depth-2>.site-navigation-branch-link{min-height:1.8rem;font-size:.75rem;font-weight:620;color:var(--site-navigation-muted)}
.site-navigation-marker{display:inline-grid;flex:none;place-items:center;color:var(--site-navigation-accent)}.site-navigation-marker-audience{width:.65rem;height:.65rem;border:1.5px solid currentColor;border-radius:999px;box-shadow:inset 0 0 0 2px var(--site-navigation-background)}.site-navigation-marker-editorial{min-width:1.45rem;border-bottom:1px solid currentColor;padding-bottom:.12rem;font-family:var(--site-heading-font,inherit);font-size:.6rem}.site-navigation-marker-index{min-width:1.7rem;font-family:ui-monospace,SFMono-Regular,monospace;font-size:.65rem}.site-navigation-marker-step{width:1.7rem;height:1.7rem;border-radius:999px;background:var(--site-navigation-accent);color:var(--site-navigation-accentText);font-size:.68rem;font-weight:900}.site-navigation-marker-room{width:1.7rem;height:1.7rem;border-radius:var(--site-navigation-radius);background:color-mix(in srgb,var(--site-navigation-accent) 12%,transparent)}.site-navigation-marker-diamond{width:.45rem;height:.45rem;transform:rotate(45deg);background:var(--site-navigation-accent)}.site-navigation-marker-dot{width:.55rem;height:.55rem;border-radius:999px;background:var(--site-navigation-accent)}.site-navigation-marker-code{width:2rem;height:2rem;border-radius:var(--site-navigation-radius);background:color-mix(in srgb,var(--site-navigation-accent) 12%,transparent);font-size:.58rem;font-weight:900;text-transform:uppercase}
.site-navigation-feature{display:flex;min-height:14rem;flex-direction:column;justify-content:space-between;gap:2rem;margin:1rem;border-radius:var(--site-navigation-radius);background:var(--site-navigation-surface);padding:1.35rem;color:var(--site-navigation-text);transition:transform .18s}.site-navigation-feature:hover{transform:translateY(-2px)}.site-navigation-feature-kicker{display:flex;justify-content:space-between;font-size:.65rem;font-weight:850;letter-spacing:.14em;color:var(--site-navigation-muted)}.site-navigation-feature-copy strong{display:block;font-family:var(--site-heading-font,inherit);font-size:1.2rem;line-height:1.1}.site-navigation-feature-tags{display:flex;flex-wrap:wrap;gap:.35rem;margin-top:.8rem}.site-navigation-feature-tags span{border:1px solid var(--site-navigation-border);border-radius:999px;padding:.22rem .45rem;font-size:.64rem}
.site-navigation-department{grid-template-columns:13rem minmax(0,1fr) 15rem}.site-navigation-department-rail{display:grid;align-content:start;gap:.2rem;border-right:1px solid var(--site-navigation-border);background:var(--site-navigation-surface);padding:1.15rem}.site-navigation-rail-title{margin-bottom:.5rem;font-family:var(--site-heading-font,inherit);font-weight:850}.site-navigation-rail-link{display:flex;min-height:2.6rem;align-items:center;gap:.6rem;border-radius:var(--site-navigation-radius);padding:.35rem .5rem;font-size:.75rem;font-weight:730}.site-navigation-rail-link:hover{background:var(--site-navigation-background);color:var(--site-navigation-accent)}
.site-navigation-panel-audienceMega{background:color-mix(in srgb,var(--site-navigation-background) 94%,transparent);backdrop-filter:blur(16px)}.site-navigation-audience{grid-template-columns:minmax(13rem,.45fr) minmax(0,1.55fr)}.site-navigation-audience-lead{border-right:1px solid var(--site-navigation-border);background:var(--site-navigation-surface);padding:clamp(1.25rem,2.5vw,2.35rem)}.site-navigation-audience-lead>.site-navigation-branch,.site-navigation-audience-lead>ul>.site-navigation-branch{border-left:3px solid var(--site-navigation-accent);padding-left:.75rem}.site-navigation-tree-audienceMega{grid-template-columns:repeat(3,minmax(0,1fr))}.site-navigation-tree-audienceMega>.site-navigation-branch{border-left:2px solid color-mix(in srgb,var(--site-navigation-accent) 25%,transparent);padding-left:.6rem}
.site-navigation-panel-editorialOverlay,.site-navigation-panel-technicalIndex{border-radius:0;background:var(--site-navigation-background)}.site-navigation-editorial{grid-template-columns:minmax(0,1.65fr) minmax(15rem,.6fr)}.site-navigation-editorial-grid{display:grid;grid-template-columns:minmax(12rem,.7fr) minmax(0,1.3fr);gap:2rem}.site-navigation-editorial-lead{border-block:1px solid var(--site-navigation-border);padding-block:1rem}.site-navigation-tree-editorialOverlay{grid-template-columns:repeat(2,minmax(0,1fr));gap:1.4rem}.site-navigation-tree-editorialOverlay>.site-navigation-branch{border-top:1px solid var(--site-navigation-border);padding-top:.6rem}
.site-navigation-panel-roomPlanner{background:linear-gradient(135deg,var(--site-navigation-background),var(--site-navigation-surface))}.site-navigation-room-planner{display:block}.site-navigation-tree-roomPlanner{grid-template-columns:repeat(4,minmax(0,1fr));gap:.65rem}.site-navigation-tree-roomPlanner>.site-navigation-branch{border:1px solid var(--site-navigation-border);border-radius:var(--site-navigation-radius);background:var(--site-navigation-surface);padding:.7rem}
.site-navigation-panel-routineMega{background:linear-gradient(135deg,color-mix(in srgb,var(--site-navigation-accent) 9%,var(--site-navigation-background)),var(--site-navigation-background))}.site-navigation-routine-path{display:block}.site-navigation-tree-routineMega{grid-template-columns:repeat(3,minmax(0,1fr));gap:.7rem}.site-navigation-tree-routineMega>.site-navigation-branch{position:relative;border:1px solid color-mix(in srgb,var(--site-navigation-accent) 20%,var(--site-navigation-border));border-radius:var(--site-navigation-radius);background:var(--site-navigation-surface);padding:.65rem}.site-navigation-tree-routineMega>.site-navigation-branch:not(:last-child)::after{position:absolute;top:1.5rem;right:-.55rem;width:.4rem;height:1px;background:var(--site-navigation-accent);content:""}
.site-navigation-concierge{grid-template-columns:minmax(0,1.5fr) minmax(16rem,.55fr)}.site-navigation-panel-collectionConcierge{border-radius:min(var(--site-navigation-radius),4px)}.site-navigation-tree-collectionConcierge{grid-template-columns:repeat(2,minmax(0,1fr));gap:1.5rem 2.5rem}.site-navigation-tree-collectionConcierge>.site-navigation-branch{border-bottom:1px solid var(--site-navigation-border);padding-bottom:1rem}.site-navigation-panel-collectionConcierge .site-navigation-panel-main{padding:2.5rem 3rem}
.site-navigation-panel-groceryHub{border-width:2px}.site-navigation-grocery-hub{display:grid;grid-template-columns:minmax(12rem,.35fr) minmax(0,1.65fr)}.site-navigation-grocery-hub.is-compact{display:block}.site-navigation-grocery-aisles{border-right:1px solid var(--site-navigation-border);background:var(--site-navigation-surface);padding:1rem}.site-navigation-grocery-aisles>ul{display:grid;gap:.35rem}.site-navigation-grocery-aisles .site-navigation-branch{border:1px solid var(--site-navigation-border);border-radius:var(--site-navigation-radius);background:var(--site-navigation-background);padding:.35rem}.site-navigation-tree-groceryHub{grid-template-columns:repeat(5,minmax(0,1fr));gap:.55rem}.site-navigation-tree-groceryHub>.site-navigation-branch{border:1px solid var(--site-navigation-border);border-radius:var(--site-navigation-radius);background:var(--site-navigation-surface);padding:.55rem;text-align:center}.site-navigation-tree-groceryHub .site-navigation-branch-link{justify-content:center}
.site-navigation-story{grid-template-columns:minmax(16rem,.75fr) minmax(0,1.25fr)}.site-navigation-story>.site-navigation-feature{min-height:100%;margin:0;border-radius:0;border-right:1px solid var(--site-navigation-border)}.site-navigation-tree-productStory{grid-template-columns:repeat(2,minmax(0,1fr));gap:0}.site-navigation-tree-productStory>.site-navigation-branch{border-top:1px solid var(--site-navigation-border);padding:1rem}
.site-navigation-pill-hub{display:block}.site-navigation-pill-clusters{display:flex;flex-wrap:wrap;align-items:flex-start;gap:.65rem}.site-navigation-pill-clusters>.site-navigation-branch{position:relative;flex:0 1 16rem;border:1px solid var(--site-navigation-border);border-radius:var(--site-navigation-radius);background:var(--site-navigation-surface);padding:.45rem .6rem}.site-navigation-pill-clusters>.site-navigation-branch>.site-navigation-branch-link{border-radius:999px}.site-navigation-pill-clusters .site-navigation-children{position:static;display:grid;margin:.35rem 0 0!important;border:0;border-top:1px solid var(--site-navigation-border);border-radius:0;background:transparent;box-shadow:none;padding:.4rem 0 0!important}
.site-navigation-technical{grid-template-columns:minmax(0,1fr) 17rem;font-family:ui-monospace,SFMono-Regular,monospace}.site-navigation-tree-technicalIndex{grid-template-columns:repeat(4,minmax(0,1fr))}.site-navigation-tree-technicalIndex>.site-navigation-branch{border-top:1px solid var(--site-navigation-border);padding-top:.6rem}.site-navigation-technical-aside{display:grid;align-content:space-between;border-left:1px solid var(--site-navigation-border);padding:1rem;font-size:.67rem;letter-spacing:.12em}.site-navigation-technical-aside .site-navigation-feature{margin:0;font-family:var(--site-body-font,inherit);letter-spacing:normal}
.site-navigation-laguna-classic-shell{border-block:1px solid var(--site-navigation-border);background:var(--site-navigation-background)}.site-navigation-root .site-navigation-laguna-classic-roots{display:flex;min-height:52px;max-width:var(--site-container,1200px);align-items:center;gap:2rem;margin-inline:auto;padding-inline:1.5rem}.site-navigation-laguna-classic-root-item{position:relative;align-self:stretch}.site-navigation-laguna-classic-root-control{display:flex;height:52px;align-items:stretch}.site-navigation-laguna-classic-root-link{position:relative;display:flex;height:52px;align-items:center;border:0;background:transparent;padding:1rem 0;color:inherit;text-align:inherit;font:inherit;font-size:.875rem;font-weight:700;white-space:nowrap;cursor:pointer;transition:color .18s}.site-navigation-laguna-classic-root-toggle{display:grid;width:1.75rem;flex:none;place-items:center;border:0;background:transparent;padding:.25rem;color:inherit;cursor:pointer}.site-navigation-laguna-classic-root-toggle .site-navigation-trigger-chevron{width:.65rem;height:.65rem}.site-navigation-laguna-classic-root-toggle[aria-expanded=true] .site-navigation-trigger-chevron{transform:rotate(180deg)}.site-navigation-laguna-classic-root-link::after{position:absolute;inset-inline:0;bottom:.5rem;height:2px;transform:scaleX(0);transform-origin:left;background:currentColor;content:"";transition:transform .2s}.site-navigation-laguna-classic-root-link:hover::after,.site-navigation-laguna-classic-root-link:focus-visible::after,.site-navigation-laguna-classic-root-item.is-active .site-navigation-laguna-classic-root-link::after,.site-navigation-laguna-classic-root-item.is-selected .site-navigation-laguna-classic-root-link::after{transform:scaleX(1)}.site-navigation-laguna-classic-root-link:hover,.site-navigation-laguna-classic-root-toggle:hover,.site-navigation-laguna-classic-root-item.is-active .site-navigation-laguna-classic-root-link,.site-navigation-laguna-classic-root-item.is-selected .site-navigation-laguna-classic-root-link,.site-navigation-laguna-classic-root-item.is-sale .site-navigation-laguna-classic-root-link{color:var(--site-navigation-accent)}.site-navigation-laguna-classic-sub-row{position:relative;border-top:1px solid var(--site-navigation-border)}.site-navigation-laguna-classic-root-target-wrap{display:flex;max-width:var(--site-container,1200px);justify-content:flex-end;margin-inline:auto;padding:.5rem 1.5rem 0}.site-navigation-laguna-classic-root-target{display:inline-flex;min-height:2rem;align-items:center;gap:.45rem;color:var(--site-navigation-accent);font-size:.72rem;font-weight:800}.site-navigation-laguna-classic-inline-chevron,.site-navigation-laguna-classic-mobile-inline-chevron{position:relative;display:block;width:.7rem;height:.7rem;flex:none}.site-navigation-laguna-classic-inline-chevron::before,.site-navigation-laguna-classic-mobile-inline-chevron::before{position:absolute;top:50%;left:45%;width:.35rem;height:.35rem;border-right:1.5px solid currentColor;border-bottom:1.5px solid currentColor;content:"";transform:translate(-50%,-50%) rotate(-45deg)}.site-navigation-laguna-classic-sub-row>.site-navigation-desktop-shell>.site-navigation-primary{min-height:52px;max-width:var(--site-container,1200px);gap:1.75rem;margin-inline:auto;padding-inline:1.5rem}.site-navigation-laguna-classic-sub-row .site-navigation-primary-item{display:flex;align-self:stretch;align-items:center}.site-navigation-laguna-classic-sub-row .site-navigation-primary-trigger,.site-navigation-laguna-classic-sub-row .site-navigation-primary-link{position:relative;min-height:52px;gap:.25rem;border-radius:0;background:transparent;padding:1rem 0;font-size:.8125rem;font-weight:700}.site-navigation-laguna-classic-sub-row .site-navigation-primary-toggle{min-height:52px;width:1.65rem;border-radius:0;padding:.25rem}.site-navigation-laguna-classic-sub-row .site-navigation-primary-trigger::after,.site-navigation-laguna-classic-sub-row .site-navigation-primary-link::after{position:absolute;inset-inline:0;bottom:.5rem;height:2px;transform:scaleX(0);transform-origin:left;background:currentColor;content:"";transition:transform .2s}.site-navigation-laguna-classic-sub-row .site-navigation-primary-trigger:hover::after,.site-navigation-laguna-classic-sub-row .site-navigation-primary-trigger:focus-visible::after,.site-navigation-laguna-classic-sub-row .site-navigation-primary-link:hover::after,.site-navigation-laguna-classic-sub-row .site-navigation-primary-link:focus-visible::after,.site-navigation-laguna-classic-sub-row .site-navigation-primary-item.is-active>.site-navigation-primary-trigger::after,.site-navigation-laguna-classic-sub-row .site-navigation-primary-item.is-active>.site-navigation-primary-link::after,.site-navigation-laguna-classic-sub-row .site-navigation-primary-item.is-open>.site-navigation-primary-trigger::after{transform:scaleX(1)}.site-navigation-laguna-classic-sub-row .site-navigation-primary-trigger:hover,.site-navigation-laguna-classic-sub-row .site-navigation-primary-link:hover,.site-navigation-laguna-classic-sub-row .site-navigation-primary-item.is-active>.site-navigation-primary-trigger,.site-navigation-laguna-classic-sub-row .site-navigation-primary-item.is-active>.site-navigation-primary-link,.site-navigation-laguna-classic-sub-row .site-navigation-primary-item.is-open>.site-navigation-primary-trigger,.site-navigation-laguna-classic-sub-row .site-navigation-primary-item.is-sale>.site-navigation-primary-trigger,.site-navigation-laguna-classic-sub-row .site-navigation-primary-item.is-sale>.site-navigation-primary-link{background:transparent;color:var(--site-navigation-accent)}.site-navigation-laguna-classic-sub-row .site-navigation-trigger-chevron{position:relative;display:block;width:.875rem;height:.875rem;flex:none;font-size:0;transform:none}.site-navigation-laguna-classic-sub-row .site-navigation-trigger-chevron::before{position:absolute;top:50%;left:50%;width:.4375rem;height:.4375rem;border-right:1.5px solid currentColor;border-bottom:1.5px solid currentColor;content:"";transform:translate(-50%,-68%) rotate(45deg);transition:transform .18s}.site-navigation-laguna-classic-sub-row .site-navigation-primary-item.is-open>.site-navigation-primary-trigger .site-navigation-trigger-chevron{transform:none}.site-navigation-laguna-classic-sub-row .site-navigation-primary-item.is-open>.site-navigation-primary-trigger .site-navigation-trigger-chevron::before{transform:translate(-50%,-32%) rotate(225deg)}.site-navigation-laguna-classic-sub-row .site-navigation-panel-wrap{top:100%;width:min(var(--site-container,1200px),var(--site-navigation-panel-viewport-width,calc(100vw - 3rem)));padding-top:.5rem}
.site-navigation-panel-lagunaClassic{max-height:none;overflow:visible;border:0;border-radius:28px;background:transparent;box-shadow:none;backdrop-filter:none}.site-navigation-laguna-classic-grid{display:grid;overflow:hidden;gap:1.25rem;border:1px solid color-mix(in srgb,var(--site-navigation-text) 5%,transparent);border-radius:28px;background:color-mix(in srgb,var(--site-navigation-background) 95%,transparent);padding:2rem 1.5rem;box-shadow:var(--site-navigation-shadow);backdrop-filter:blur(16px)}.site-navigation-laguna-classic-grid-1{grid-template-columns:minmax(16.25rem,21.25rem);justify-content:start}.site-navigation-laguna-classic-grid-1.is-split{grid-template-columns:minmax(32.5rem,38.75rem)}.site-navigation-laguna-classic-grid-2{grid-template-columns:repeat(2,minmax(0,1fr))}.site-navigation-laguna-classic-grid-3{grid-template-columns:repeat(3,minmax(0,1fr))}.site-navigation-laguna-classic-grid-4{grid-template-columns:repeat(4,minmax(0,1fr))}.site-navigation-laguna-classic-column{position:relative;min-width:0;padding-inline:1.25rem}.site-navigation-laguna-classic-divider{position:absolute;top:50%;left:-.625rem;width:1px;height:72%;transform:translateY(-50%);background:color-mix(in srgb,var(--site-navigation-text) 5%,transparent)}.site-navigation-laguna-classic-title{position:relative;display:block;width:max-content;max-width:100%;overflow:hidden;text-overflow:ellipsis;color:inherit;font-size:.875rem;font-weight:900;text-transform:uppercase;white-space:nowrap}.site-navigation-laguna-classic-title::after,.site-navigation-laguna-classic-overflow-link::after{position:absolute;inset-inline:0;bottom:-.25rem;height:2px;transform:scaleX(0);transform-origin:left;background:currentColor;content:"";transition:transform .2s}.site-navigation-laguna-classic-title:hover,.site-navigation-laguna-classic-title:focus-visible,.site-navigation-laguna-classic-overflow-link:hover,.site-navigation-laguna-classic-overflow-link:focus-visible{color:var(--site-navigation-accent)}.site-navigation-laguna-classic-title:hover::after,.site-navigation-laguna-classic-title:focus-visible::after,.site-navigation-laguna-classic-overflow-link:hover::after,.site-navigation-laguna-classic-overflow-link:focus-visible::after{transform:scaleX(1)}.site-navigation-laguna-classic-groups{display:grid;gap:.5rem}.site-navigation-laguna-classic-groups.has-title{margin-top:1.25rem}.site-navigation-laguna-classic-groups.is-split{grid-template-columns:repeat(2,minmax(0,1fr));column-gap:1.5rem}.site-navigation-laguna-classic-group{display:grid;align-content:start;gap:.5rem}.site-navigation-laguna-classic-branch{min-width:0}.site-navigation-laguna-classic-link{display:flex;min-height:3rem;width:100%;max-width:16.875rem;align-items:center;gap:.625rem;border-radius:.75rem;padding:.375rem .5rem;color:var(--site-navigation-muted);font-size:.875rem;font-weight:700;transition:background .18s,color .18s,box-shadow .18s}.site-navigation-laguna-classic-link:hover,.site-navigation-laguna-classic-link:focus-visible,.site-navigation-laguna-classic-link.is-active{background:var(--site-navigation-surface);box-shadow:0 1px 3px color-mix(in srgb,var(--site-navigation-text) 10%,transparent);color:var(--site-navigation-accent)}.site-navigation-laguna-classic-link.is-sale{color:var(--site-navigation-accent)}.site-navigation-laguna-classic-marker{display:grid;width:2.25rem;height:2.25rem;flex:none;place-items:center;border-radius:999px;background:color-mix(in srgb,var(--site-navigation-accent) 11%,var(--site-navigation-surface));color:var(--site-navigation-accent);font-size:.7rem;font-weight:900;text-transform:uppercase;box-shadow:inset 0 0 0 1px var(--site-navigation-border)}.site-navigation-laguna-classic-label{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.site-navigation-laguna-classic-overflow{display:flex;flex-wrap:wrap;gap:.5rem 1.75rem;border-top:1px solid color-mix(in srgb,var(--site-navigation-text) 5%,transparent);background:var(--site-navigation-surface);padding:1rem 1.5rem;color:var(--site-navigation-muted);font-size:.75rem;font-weight:900;text-transform:uppercase}.site-navigation-laguna-classic-overflow-link{position:relative}.site-navigation-laguna-classic-overflow-label{display:inline-block}
.site-navigation-laguna-classic-mobile{margin:-1rem -1rem 0}.site-navigation-laguna-classic-tabs{display:flex;overflow-x:auto;border-bottom:1px solid var(--site-navigation-border)}.site-navigation-laguna-classic-tab-item{display:flex;min-width:7rem;flex:1}.site-navigation-laguna-classic-tab-control{display:flex;min-width:0;width:100%;align-items:stretch}.site-navigation-laguna-classic-tab{position:relative;display:flex;min-width:0;width:100%;flex:1;align-items:center;justify-content:center;border:0;border-bottom:2px solid transparent;background:transparent;padding:1rem .75rem;color:inherit;font:inherit;font-size:.875rem;font-weight:700;white-space:nowrap;cursor:pointer;transition:border-color .18s,color .18s}.site-navigation-laguna-classic-tab-link{overflow:hidden;text-overflow:ellipsis}.site-navigation-laguna-classic-tab-toggle{display:grid;width:2rem;flex:none;place-items:center;border:0;border-bottom:2px solid transparent;background:transparent;padding:.25rem;color:inherit;cursor:pointer}.site-navigation-laguna-classic-tab:hover,.site-navigation-laguna-classic-tab:focus-visible,.site-navigation-laguna-classic-tab-toggle:hover,.site-navigation-laguna-classic-tab-toggle:focus-visible,.site-navigation-laguna-classic-tab-item.is-selected :is(.site-navigation-laguna-classic-tab,.site-navigation-laguna-classic-tab-toggle){border-bottom-color:var(--site-navigation-accent);color:var(--site-navigation-accent)}.site-navigation-laguna-classic-tab-toggle[aria-expanded=true] .site-navigation-mobile-chevron{transform:rotate(180deg)}.site-navigation-laguna-classic-mobile-panel{padding:1rem}.site-navigation-laguna-classic-mobile-root-link,.site-navigation-laguna-classic-mobile-category-link{display:flex;min-height:3.25rem;align-items:center;justify-content:space-between;gap:1rem;border-bottom:1px solid var(--site-navigation-border);font-weight:700}.site-navigation-laguna-classic-mobile-root-link{font-weight:700}.site-navigation-laguna-classic-mobile-root-link:hover,.site-navigation-laguna-classic-mobile-category-link:hover,.site-navigation-laguna-classic-mobile-root-link:focus-visible,.site-navigation-laguna-classic-mobile-category-link:focus-visible,.site-navigation-laguna-classic-mobile-root-link.is-sale,.site-navigation-laguna-classic-mobile-category-link.is-sale{color:var(--site-navigation-accent)}.site-navigation-laguna-classic-mobile-children{display:grid;gap:.75rem;border-bottom:1px solid var(--site-navigation-border);padding:.25rem 0 1rem 1rem}.site-navigation-laguna-classic-mobile-child-link{display:block;width:max-content;max-width:100%;color:var(--site-navigation-muted);font-size:.875rem}.site-navigation-laguna-classic-mobile-child-link:hover,.site-navigation-laguna-classic-mobile-child-link:focus-visible,.site-navigation-laguna-classic-mobile-child-link.is-active,.site-navigation-laguna-classic-mobile-child-link.is-sale{color:var(--site-navigation-accent)}.site-navigation-laguna-classic-mobile-more{display:grid;gap:1rem;margin-top:1.5rem;font-weight:700}.site-navigation-laguna-classic-mobile-more-link{position:relative;display:block;width:max-content;max-width:100%}.site-navigation-laguna-classic-mobile-more-link:hover,.site-navigation-laguna-classic-mobile-more-link:focus-visible,.site-navigation-laguna-classic-mobile-more-link.is-sale{color:var(--site-navigation-accent)}
.site-navigation-mobile-tree{display:grid;gap:.35rem}.site-navigation-mobile-item{min-width:0}.site-navigation-mobile-trigger,.site-navigation-mobile-link{display:flex;width:100%;min-width:0;max-width:100%;min-height:48px;align-items:center;gap:.7rem;border:1px solid transparent;border-radius:var(--site-navigation-radius);background:transparent;padding:.7rem .8rem;color:inherit;text-align:left;font:inherit;font-size:.88rem;font-weight:780}.site-navigation-mobile-label{min-width:0;max-width:100%;flex:1;white-space:normal;overflow-wrap:anywhere}.site-navigation-mobile-arrow{flex:none}.site-navigation-mobile-trigger{cursor:pointer}.site-navigation-mobile-trigger:hover,.site-navigation-mobile-link:hover,.site-navigation-mobile-item.is-active>.site-navigation-mobile-trigger,.site-navigation-mobile-item.is-active>.site-navigation-mobile-link{border-color:var(--site-navigation-border);background:var(--site-navigation-surface);color:var(--site-navigation-accent)}.site-navigation-mobile-chevron{position:relative;display:block;width:.75rem;height:.75rem;flex:none;margin-left:auto;transition:transform .18s}.site-navigation-mobile-chevron::before{position:absolute;top:50%;left:50%;width:.375rem;height:.375rem;border-right:1.5px solid currentColor;border-bottom:1.5px solid currentColor;content:"";transform:translate(-50%,-68%) rotate(45deg)}.site-navigation-mobile-trigger[aria-expanded=true] .site-navigation-mobile-chevron{transform:rotate(180deg)}.site-navigation-mobile-panel{margin:.2rem 0 .4rem .8rem;border-left:1px solid var(--site-navigation-border);padding-left:.6rem}.site-navigation-mobile-show-all{display:flex;min-width:0;max-width:100%;min-height:40px;align-items:center;padding:.35rem .75rem;color:var(--site-navigation-accent);font-size:.76rem;font-weight:820}.site-navigation-mobile-panel .site-navigation-mobile-trigger,.site-navigation-mobile-panel .site-navigation-mobile-link{min-height:42px;font-size:.78rem}.site-navigation-mobile-technicalIndex{font-family:ui-monospace,SFMono-Regular,monospace}.site-navigation-mobile-categoryPills>.site-navigation-mobile-item>.site-navigation-mobile-trigger,.site-navigation-mobile-categoryPills>.site-navigation-mobile-item>.site-navigation-mobile-link{border-color:var(--site-navigation-border);border-radius:999px}.site-navigation-mobile-routineMega>.site-navigation-mobile-item>.site-navigation-mobile-trigger{background:color-mix(in srgb,var(--site-navigation-accent) 8%,transparent)}
.site-navigation-mobile-parent{display:flex;min-width:0;width:100%;align-items:stretch;border-radius:var(--site-navigation-radius)}.site-navigation-mobile-parent-link{min-width:0;width:auto;flex:1;border-radius:var(--site-navigation-radius) 0 0 var(--site-navigation-radius);padding-right:.35rem}.site-navigation-mobile-toggle{display:grid;min-height:48px;width:2.75rem;flex:none;place-items:center;border:1px solid transparent;border-left:0;border-radius:0 var(--site-navigation-radius) var(--site-navigation-radius) 0;background:transparent;padding:.5rem;color:inherit;cursor:pointer}.site-navigation-mobile-parent:hover,.site-navigation-mobile-item.is-active>.site-navigation-mobile-parent{border-color:var(--site-navigation-border);background:var(--site-navigation-surface);color:var(--site-navigation-accent)}.site-navigation-mobile-parent:hover>.site-navigation-mobile-link,.site-navigation-mobile-item.is-active>.site-navigation-mobile-parent>.site-navigation-mobile-link{background:transparent;color:inherit}.site-navigation-mobile-toggle[aria-expanded=true] .site-navigation-mobile-chevron{transform:rotate(180deg)}.site-navigation-mobile-panel .site-navigation-mobile-toggle{min-height:42px}.site-navigation-mobile-categoryPills>.site-navigation-mobile-item>.site-navigation-mobile-parent{border:1px solid var(--site-navigation-border);border-radius:999px}.site-navigation-mobile-categoryPills>.site-navigation-mobile-item>.site-navigation-mobile-parent .site-navigation-mobile-link{border:0}.site-navigation-mobile-routineMega>.site-navigation-mobile-item>.site-navigation-mobile-parent{background:color-mix(in srgb,var(--site-navigation-accent) 8%,transparent)}

/* Web-only refinement layer. Shop intentionally does not receive this class. */
.web-site-navigation.site-navigation-root{--web-navigation-hairline:color-mix(in srgb,var(--site-navigation-accent) 28%,var(--site-navigation-border));--web-navigation-glow:color-mix(in srgb,var(--site-navigation-accent) 13%,transparent)}
.web-site-navigation:not(:is([data-site-navigation-variant=lagunaClassic],[data-site-navigation-variant=industrialBar],[data-site-navigation-variant=cinematicOverlay],[data-site-navigation-variant=minimalReveal],[data-site-navigation-variant=boldUnderline],[data-site-navigation-variant=editorialRule],[data-site-navigation-variant=playfulFrame],[data-site-navigation-variant=neonLine])) .site-navigation-primary-trigger,.web-site-navigation:not(:is([data-site-navigation-variant=lagunaClassic],[data-site-navigation-variant=industrialBar],[data-site-navigation-variant=cinematicOverlay],[data-site-navigation-variant=minimalReveal],[data-site-navigation-variant=boldUnderline],[data-site-navigation-variant=editorialRule],[data-site-navigation-variant=playfulFrame],[data-site-navigation-variant=neonLine])) .site-navigation-primary-link{position:relative;isolation:isolate;min-height:44px;overflow:hidden;letter-spacing:.015em}
.web-site-navigation:not(:is([data-site-navigation-variant=lagunaClassic],[data-site-navigation-variant=industrialBar],[data-site-navigation-variant=cinematicOverlay],[data-site-navigation-variant=minimalReveal],[data-site-navigation-variant=boldUnderline],[data-site-navigation-variant=editorialRule],[data-site-navigation-variant=playfulFrame],[data-site-navigation-variant=neonLine])) .site-navigation-primary-trigger::after,.web-site-navigation:not(:is([data-site-navigation-variant=lagunaClassic],[data-site-navigation-variant=industrialBar],[data-site-navigation-variant=cinematicOverlay],[data-site-navigation-variant=minimalReveal],[data-site-navigation-variant=boldUnderline],[data-site-navigation-variant=editorialRule],[data-site-navigation-variant=playfulFrame],[data-site-navigation-variant=neonLine])) .site-navigation-primary-link::after{position:absolute;right:.65rem;bottom:.3rem;left:.65rem;height:2px;transform:scaleX(0);transform-origin:left;background:var(--site-navigation-accent);content:"";transition:transform .24s ease}
.web-site-navigation:not(:is([data-site-navigation-variant=lagunaClassic],[data-site-navigation-variant=industrialBar],[data-site-navigation-variant=cinematicOverlay],[data-site-navigation-variant=minimalReveal],[data-site-navigation-variant=boldUnderline],[data-site-navigation-variant=editorialRule],[data-site-navigation-variant=playfulFrame],[data-site-navigation-variant=neonLine])) .site-navigation-primary-trigger:hover::after,.web-site-navigation:not(:is([data-site-navigation-variant=lagunaClassic],[data-site-navigation-variant=industrialBar],[data-site-navigation-variant=cinematicOverlay],[data-site-navigation-variant=minimalReveal],[data-site-navigation-variant=boldUnderline],[data-site-navigation-variant=editorialRule],[data-site-navigation-variant=playfulFrame],[data-site-navigation-variant=neonLine])) .site-navigation-primary-link:hover::after,.web-site-navigation:not(:is([data-site-navigation-variant=lagunaClassic],[data-site-navigation-variant=industrialBar],[data-site-navigation-variant=cinematicOverlay],[data-site-navigation-variant=minimalReveal],[data-site-navigation-variant=boldUnderline],[data-site-navigation-variant=editorialRule],[data-site-navigation-variant=playfulFrame],[data-site-navigation-variant=neonLine])) .site-navigation-primary-item.is-open>.site-navigation-primary-trigger::after,.web-site-navigation:not(:is([data-site-navigation-variant=lagunaClassic],[data-site-navigation-variant=industrialBar],[data-site-navigation-variant=cinematicOverlay],[data-site-navigation-variant=minimalReveal],[data-site-navigation-variant=boldUnderline],[data-site-navigation-variant=editorialRule],[data-site-navigation-variant=playfulFrame],[data-site-navigation-variant=neonLine])) .site-navigation-primary-item.is-active>.site-navigation-primary-link::after{transform:scaleX(1)}
.web-site-navigation:not(:is([data-site-navigation-variant=lagunaClassic],[data-site-navigation-variant=industrialBar],[data-site-navigation-variant=cinematicOverlay],[data-site-navigation-variant=minimalReveal],[data-site-navigation-variant=boldUnderline],[data-site-navigation-variant=editorialRule],[data-site-navigation-variant=playfulFrame],[data-site-navigation-variant=neonLine])) .site-navigation-panel{border-color:color-mix(in srgb,var(--site-navigation-text) 11%,var(--site-navigation-border));box-shadow:0 26px 72px color-mix(in srgb,var(--site-navigation-text) 19%,transparent),0 2px 0 var(--web-navigation-hairline);backdrop-filter:blur(20px) saturate(1.08)}
.web-site-navigation .site-navigation-panel-heading{position:relative}.web-site-navigation .site-navigation-panel-heading::after{position:absolute;right:0;bottom:-1px;width:clamp(2.5rem,8vw,7rem);height:2px;background:var(--site-navigation-accent);content:""}.web-site-navigation .site-navigation-branch-link{transition:background .2s,color .2s,transform .2s}.web-site-navigation .site-navigation-branch-link:hover{transform:translateX(3px)}.web-site-navigation .site-navigation-feature{overflow:hidden;border:1px solid color-mix(in srgb,var(--site-navigation-accent) 16%,var(--site-navigation-border));box-shadow:inset 0 1px color-mix(in srgb,var(--site-navigation-background) 70%,transparent)}
.web-site-navigation>.site-navigation-mobile-tree{gap:.2rem}.web-site-navigation>.site-navigation-mobile-tree>.site-navigation-mobile-item{border-bottom:1px solid color-mix(in srgb,var(--site-navigation-text) 9%,var(--site-navigation-border))}.web-site-navigation>.site-navigation-mobile-tree>.site-navigation-mobile-item:last-child{border-bottom:0}.web-site-navigation .site-navigation-mobile-trigger,.web-site-navigation .site-navigation-mobile-link{transition:background .2s,color .2s,border-color .2s,transform .2s}.web-site-navigation .site-navigation-mobile-trigger:active,.web-site-navigation .site-navigation-mobile-link:active{transform:translateX(3px)}

.web-site-navigation[data-site-navigation-variant=departmentMega] .site-navigation-panel{border-radius:2px;border-top:3px solid var(--site-navigation-accent)}.web-site-navigation[data-site-navigation-variant=departmentMega] .site-navigation-department-rail{position:relative;background:linear-gradient(180deg,var(--site-navigation-surface),color-mix(in srgb,var(--site-navigation-accent) 5%,var(--site-navigation-surface)))}.web-site-navigation[data-site-navigation-variant=departmentMega] .site-navigation-department-rail::before{position:absolute;top:0;bottom:0;left:0;width:3px;background:var(--site-navigation-accent);content:""}.web-site-navigation[data-site-navigation-variant=departmentMega] .site-navigation-mobile-tree>.site-navigation-mobile-item{border-radius:0}
.web-site-navigation[data-site-navigation-variant=audienceMega] .site-navigation-panel{border-radius:clamp(1rem,var(--site-navigation-radius),1.75rem);background:color-mix(in srgb,var(--site-navigation-background) 91%,transparent)}.web-site-navigation[data-site-navigation-variant=audienceMega] .site-navigation-audience-lead{background:radial-gradient(circle at 10% 10%,var(--web-navigation-glow),transparent 55%),var(--site-navigation-surface)}.web-site-navigation[data-site-navigation-variant=audienceMega] .site-navigation-mobile-trigger,.web-site-navigation[data-site-navigation-variant=audienceMega] .site-navigation-mobile-link{border-radius:999px}
.web-site-navigation[data-site-navigation-variant=editorialOverlay] .site-navigation-panel{border-width:0 0 3px;border-color:var(--site-navigation-accent);background:linear-gradient(115deg,var(--site-navigation-background) 0 72%,color-mix(in srgb,var(--site-navigation-accent) 8%,var(--site-navigation-background)) 72%)}.web-site-navigation[data-site-navigation-variant=editorialOverlay] .site-navigation-panel-heading strong{font-size:clamp(1.45rem,3vw,2.65rem);font-weight:580}.web-site-navigation[data-site-navigation-variant=editorialOverlay] .site-navigation-mobile-tree>.site-navigation-mobile-item{border-bottom:1px solid var(--site-navigation-border)}
.web-site-navigation[data-site-navigation-variant=roomPlanner] .site-navigation-panel{background-color:var(--site-navigation-background);background-image:linear-gradient(var(--site-navigation-border) 1px,transparent 1px),linear-gradient(90deg,var(--site-navigation-border) 1px,transparent 1px);background-size:2rem 2rem}.web-site-navigation[data-site-navigation-variant=roomPlanner] .site-navigation-tree-roomPlanner>.site-navigation-branch{box-shadow:0 8px 24px color-mix(in srgb,var(--site-navigation-text) 7%,transparent)}.web-site-navigation[data-site-navigation-variant=roomPlanner] .site-navigation-mobile-trigger,.web-site-navigation[data-site-navigation-variant=roomPlanner] .site-navigation-mobile-link{border-radius:2px}
.web-site-navigation[data-site-navigation-variant=routineMega] .site-navigation-tree-routineMega>.site-navigation-branch{box-shadow:0 10px 28px color-mix(in srgb,var(--site-navigation-accent) 8%,transparent)}.web-site-navigation[data-site-navigation-variant=routineMega] .site-navigation-marker-step{box-shadow:0 0 0 4px color-mix(in srgb,var(--site-navigation-accent) 13%,transparent)}.web-site-navigation[data-site-navigation-variant=routineMega] .site-navigation-mobile-tree>.site-navigation-mobile-item{border-bottom:0}
.web-site-navigation[data-site-navigation-variant=collectionConcierge] .site-navigation-panel{border-top:1px solid var(--site-navigation-accent);box-shadow:0 28px 80px color-mix(in srgb,var(--site-navigation-text) 15%,transparent)}.web-site-navigation[data-site-navigation-variant=collectionConcierge] .site-navigation-panel-heading strong,.web-site-navigation[data-site-navigation-variant=collectionConcierge] .site-navigation-feature-copy strong{font-weight:520;letter-spacing:.035em}.web-site-navigation[data-site-navigation-variant=collectionConcierge] .site-navigation-mobile-link{letter-spacing:.025em}
.web-site-navigation[data-site-navigation-variant=groceryHub] .site-navigation-panel{border-color:color-mix(in srgb,var(--site-navigation-accent) 40%,var(--site-navigation-border));box-shadow:0 22px 60px color-mix(in srgb,var(--site-navigation-accent) 10%,transparent)}.web-site-navigation[data-site-navigation-variant=groceryHub] .site-navigation-tree-groceryHub>.site-navigation-branch{transition:transform .2s,box-shadow .2s}.web-site-navigation[data-site-navigation-variant=groceryHub] .site-navigation-tree-groceryHub>.site-navigation-branch:hover{transform:translateY(-3px);box-shadow:0 8px 20px color-mix(in srgb,var(--site-navigation-text) 8%,transparent)}.web-site-navigation[data-site-navigation-variant=groceryHub] .site-navigation-mobile-trigger,.web-site-navigation[data-site-navigation-variant=groceryHub] .site-navigation-mobile-link{border-radius:clamp(.5rem,var(--site-navigation-radius),1rem)}
.web-site-navigation[data-site-navigation-variant=productStory] .site-navigation-panel{background:linear-gradient(90deg,color-mix(in srgb,var(--site-navigation-accent) 8%,var(--site-navigation-background)),var(--site-navigation-background) 46%)}.web-site-navigation[data-site-navigation-variant=productStory] .site-navigation-story>.site-navigation-feature{position:relative}.web-site-navigation[data-site-navigation-variant=productStory] .site-navigation-story>.site-navigation-feature::after{position:absolute;right:-3rem;bottom:-4rem;width:10rem;height:10rem;border:1px solid var(--web-navigation-hairline);border-radius:999px;content:""}.web-site-navigation[data-site-navigation-variant=productStory] .site-navigation-mobile-tree>.site-navigation-mobile-item{border-left:2px solid transparent}.web-site-navigation[data-site-navigation-variant=productStory] .site-navigation-mobile-tree>.site-navigation-mobile-item.is-active{border-left-color:var(--site-navigation-accent)}
.web-site-navigation[data-site-navigation-variant=categoryPills] .site-navigation-pill-clusters>.site-navigation-branch{box-shadow:0 7px 18px color-mix(in srgb,var(--site-navigation-text) 5%,transparent);transition:border-color .2s,transform .2s}.web-site-navigation[data-site-navigation-variant=categoryPills] .site-navigation-pill-clusters>.site-navigation-branch:hover{border-color:var(--site-navigation-accent);transform:translateY(-2px)}.web-site-navigation[data-site-navigation-variant=categoryPills] .site-navigation-mobile-tree>.site-navigation-mobile-item{border-bottom:0}
.web-site-navigation[data-site-navigation-variant=technicalIndex] .site-navigation-panel{border-top:4px solid var(--site-navigation-accent);background:linear-gradient(135deg,var(--site-navigation-background) 0 78%,color-mix(in srgb,var(--site-navigation-accent) 7%,var(--site-navigation-background)) 78%)}.web-site-navigation[data-site-navigation-variant=technicalIndex] .site-navigation-branch-link{border-radius:0}.web-site-navigation[data-site-navigation-variant=technicalIndex] .site-navigation-mobile-trigger,.web-site-navigation[data-site-navigation-variant=technicalIndex] .site-navigation-mobile-link{border-radius:0;text-transform:uppercase;letter-spacing:.045em}
.web-site-navigation[data-site-navigation-variant=lagunaClassic] .site-navigation-laguna-classic-shell{box-shadow:0 8px 24px color-mix(in srgb,var(--site-navigation-text) 5%,transparent)}.web-site-navigation[data-site-navigation-variant=lagunaClassic] .site-navigation-laguna-classic-grid{border-color:color-mix(in srgb,var(--site-navigation-accent) 15%,var(--site-navigation-border));box-shadow:0 28px 78px color-mix(in srgb,var(--site-navigation-text) 16%,transparent)}.web-site-navigation[data-site-navigation-variant=lagunaClassic] .site-navigation-laguna-classic-marker{transition:transform .2s,box-shadow .2s}.web-site-navigation[data-site-navigation-variant=lagunaClassic] .site-navigation-laguna-classic-link:hover .site-navigation-laguna-classic-marker{transform:scale(1.06);box-shadow:inset 0 0 0 1px var(--site-navigation-accent),0 5px 14px var(--web-navigation-glow)}

.site-navigation-mobile-experience{position:relative}
/* Compact bordered text dropdown. */
.site-navigation-primary-borderedDropdown{justify-content:center;gap:clamp(1.25rem,2.5vw,3rem);padding-inline:clamp(1rem,4vw,4rem)}
.site-navigation-primary-borderedDropdown .site-navigation-primary-trigger,.site-navigation-primary-borderedDropdown .site-navigation-primary-link{min-height:46px;border-radius:0;padding-inline:0;background:transparent;font-size:.75rem;font-weight:650;text-transform:uppercase}
.site-navigation-primary-borderedDropdown .site-navigation-primary-item:is(:hover,.is-active,.is-open) :is(.site-navigation-primary-trigger,.site-navigation-primary-link,.site-navigation-primary-parent){background:transparent;color:var(--site-navigation-accent)}
.site-navigation-panel-wrap-borderedDropdown{width:min(17.5rem,var(--site-navigation-panel-viewport-width,calc(100vw - 2rem)))}
.site-navigation-panel-borderedDropdown{width:100%;overflow:visible;border:1px solid var(--site-navigation-border);border-radius:0;background:#fff;box-shadow:none;color:#111}
.site-navigation-compact-list{display:grid!important}
.site-navigation-compact-item{min-width:0}
.site-navigation-compact-item+.site-navigation-compact-item{border-top:1px solid var(--site-navigation-border)}
.site-navigation-compact-link{display:flex;min-height:32px;align-items:center;padding:.4rem 1.2rem;color:inherit;font-size:.72rem;font-weight:500;text-transform:uppercase}
.site-navigation-compact-link:hover,.site-navigation-compact-link.is-active{background:#f7f7f7;color:var(--site-navigation-accent)}
.site-navigation-compact-label{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.site-navigation-compact-children{display:grid!important;border-top:1px solid var(--site-navigation-border)}
.site-navigation-compact-children .site-navigation-compact-link{padding-left:2rem;color:#555;font-size:.68rem;text-transform:none}
.site-navigation-compact-children .site-navigation-compact-children .site-navigation-compact-link{padding-left:2.8rem}

/* Full-width white category strip with real optional category artwork. */
.site-navigation-primary-imageCategoryStrip{justify-content:center;gap:clamp(.85rem,2vw,2.4rem);padding-inline:clamp(1rem,3vw,3rem)}
.site-navigation-primary-imageCategoryStrip .site-navigation-primary-trigger,.site-navigation-primary-imageCategoryStrip .site-navigation-primary-link{min-height:46px;border:0;border-radius:0;padding-inline:.25rem;background:transparent;color:var(--site-navigation-text);font-family:var(--site-heading-font,inherit);font-size:.78rem;font-weight:450}
.site-navigation-primary-imageCategoryStrip .site-navigation-primary-item:is(:hover,.is-active,.is-open) :is(.site-navigation-primary-trigger,.site-navigation-primary-link,.site-navigation-primary-parent){border-radius:0;background:transparent;color:var(--site-navigation-text)}
.site-navigation-panel-wrap-imageCategoryStrip{top:100%;width:var(--site-navigation-panel-viewport-width,calc(100vw - 2rem))}
.site-navigation-panel-imageCategoryStrip{height:153px;overflow:hidden;border:0;border-radius:0;background:#fff;box-shadow:0 6px 12px rgb(0 0 0 / 6%);color:#111}
.site-navigation-image-category-strip{height:153px;max-width:var(--site-container,1200px);margin-inline:auto}
.site-navigation-image-strip-list{display:flex!important;height:100%;align-items:center;justify-content:safe center;gap:clamp(1.5rem,4vw,4rem);overflow-x:auto;padding:1rem 2rem!important}
.site-navigation-image-strip-item{display:flex;min-width:clamp(13rem,21vw,17rem);flex:1 0 clamp(13rem,21vw,17rem);align-items:center;gap:.65rem}
.site-navigation-image-strip-link{display:flex;min-width:0;align-items:center;gap:1.5rem;color:#111}
.site-navigation-image-strip-link:hover,.site-navigation-image-strip-link.is-active{color:var(--site-navigation-accent)}
.site-navigation-image-strip-visual{display:block;width:76px;height:76px;flex:none;background:#f5f5f5}
.site-navigation-image-strip-visual img{display:block;width:100%;height:100%;object-fit:contain}
.site-navigation-image-strip-title{font-family:var(--site-heading-font,inherit);font-size:1rem;font-weight:700;white-space:nowrap}
.site-navigation-image-strip-links,.site-navigation-image-strip-nested-list{display:none!important}
.site-navigation-image-strip-nested-link{display:block;color:#555;font-size:.66rem}
.site-navigation-image-strip-nested-link:hover,.site-navigation-image-strip-nested-link.is-active{color:var(--site-navigation-accent)}

/* Black simple text dropdown. */
.site-navigation-primary-monochromeDropdown{width:100%;gap:clamp(1rem,2vw,3rem);padding-inline:clamp(.75rem,1.25vw,1.5rem);overflow:hidden;color:#fff}
.site-navigation-primary-monochromeDropdown .site-navigation-primary-trigger,.site-navigation-primary-monochromeDropdown .site-navigation-primary-link{min-height:64px;border-radius:0;padding-inline:0;background:transparent;color:#fff;font-family:var(--site-heading-font,inherit);font-size:.9rem;font-weight:800}
.site-navigation-primary-monochromeDropdown .site-navigation-primary-item:is(:hover,.is-active,.is-open) :is(.site-navigation-primary-trigger,.site-navigation-primary-link,.site-navigation-primary-parent){background:transparent;color:#fff}
.site-navigation-panel-wrap-monochromeDropdown{top:100%;width:min(12.5rem,var(--site-navigation-panel-viewport-width,calc(100vw - 2rem)))}
.site-navigation-panel-monochromeDropdown{width:100%;overflow:visible;border:0;border-radius:0;background:#000;box-shadow:none;color:#fff}
.site-navigation-compact-dropdown-monochromeDropdown .site-navigation-compact-item+.site-navigation-compact-item,.site-navigation-compact-dropdown-monochromeDropdown .site-navigation-compact-children{border-color:#222}
.site-navigation-compact-dropdown-monochromeDropdown .site-navigation-compact-link{min-height:35px;padding-inline:1.35rem;color:#fff;font-size:.82rem;font-weight:450;text-transform:none}
.site-navigation-compact-dropdown-monochromeDropdown .site-navigation-compact-link:hover,.site-navigation-compact-dropdown-monochromeDropdown .site-navigation-compact-link.is-active{background:#181818;color:#fff}
.site-navigation-compact-dropdown-monochromeDropdown .site-navigation-compact-children .site-navigation-compact-link{color:#ddd}

.site-navigation-mobile-experience-bordered{min-height:calc(100dvh - var(--site-navigation-mobile-offset,0px));background:#fff;color:#111}
.site-navigation-mobile-borderedDropdown{gap:0}
.site-navigation-mobile-borderedDropdown>.site-navigation-mobile-item{border-bottom:1px solid #dedede}
.site-navigation-mobile-borderedDropdown :is(.site-navigation-mobile-trigger,.site-navigation-mobile-link,.site-navigation-mobile-parent,.site-navigation-mobile-toggle){border-radius:0;background:#fff;color:#111}
.site-navigation-mobile-borderedDropdown>.site-navigation-mobile-item>:is(.site-navigation-mobile-trigger,.site-navigation-mobile-link,.site-navigation-mobile-parent){min-height:56px;font-size:.8rem;font-weight:600;text-transform:uppercase}
.site-navigation-mobile-borderedDropdown .site-navigation-mobile-panel{margin:0;border-left:0;background:#fff;padding:.2rem 0 .4rem .75rem}

.site-navigation-mobile-experience-image-strip{max-width:30rem;margin-inline:auto;background:#fff;padding:.5rem;color:#111}
.site-navigation-mobile-imageCategoryStrip{gap:.5rem}
.site-navigation-mobile-imageCategoryStrip>.site-navigation-mobile-item{border:1px solid #d9d9d9;background:#fff}
.site-navigation-mobile-imageCategoryStrip :is(.site-navigation-mobile-trigger,.site-navigation-mobile-link,.site-navigation-mobile-parent,.site-navigation-mobile-toggle){border-radius:0;background:#fff;color:#111}
.site-navigation-mobile-imageCategoryStrip>.site-navigation-mobile-item>:is(.site-navigation-mobile-trigger,.site-navigation-mobile-link,.site-navigation-mobile-parent){min-height:52px;font-family:var(--site-heading-font,inherit);font-size:.88rem;font-weight:600}
.site-navigation-mobile-imageCategoryStrip .site-navigation-mobile-panel{margin:0 .5rem .5rem;border-left:0;border-top:1px solid #d9d9d9;background:#fff;padding:.25rem}

.site-navigation-mobile-experience-monochrome{min-height:calc(100dvh - var(--site-navigation-mobile-offset,0px));background:#000;color:#fff}
.site-navigation-mobile-monochromeDropdown{gap:0;background:#000}
.site-navigation-mobile-monochromeDropdown>.site-navigation-mobile-item{border-bottom:1px solid #292929}
.site-navigation-mobile-monochromeDropdown :is(.site-navigation-mobile-trigger,.site-navigation-mobile-link,.site-navigation-mobile-parent,.site-navigation-mobile-toggle){border-radius:0;border-color:transparent;background:#000;color:#fff}
.site-navigation-mobile-monochromeDropdown>.site-navigation-mobile-item>:is(.site-navigation-mobile-trigger,.site-navigation-mobile-link,.site-navigation-mobile-parent){min-height:56px;font-size:.9rem;font-weight:700}
.site-navigation-mobile-monochromeDropdown .site-navigation-mobile-panel{margin:0;border-left:0;background:#000;padding:.2rem 0 .4rem .75rem}
.site-navigation-mobile-monochromeDropdown .site-navigation-mobile-show-all{color:#fff}

/* Torque-inspired industrial blueprint. */
.site-navigation-primary-industrialBar{gap:0;border:1px solid var(--site-navigation-border);background:var(--site-navigation-background)}.site-navigation-primary-industrialBar>.site-navigation-primary-item+li{border-left:1px solid var(--site-navigation-border)}.site-navigation-primary-industrialBar .site-navigation-primary-trigger,.site-navigation-primary-industrialBar .site-navigation-primary-link{min-height:48px;border-radius:0;padding-inline:clamp(.7rem,1.4vw,1.25rem);font-family:var(--site-heading-font,inherit);font-size:.72rem;font-weight:850;letter-spacing:.075em;text-transform:uppercase}.site-navigation-primary-industrialBar .site-navigation-primary-trigger:hover,.site-navigation-primary-industrialBar .site-navigation-primary-link:hover,.site-navigation-primary-industrialBar .site-navigation-primary-item.is-active>.site-navigation-primary-trigger,.site-navigation-primary-industrialBar .site-navigation-primary-item.is-active>.site-navigation-primary-link{background:var(--site-navigation-accent);color:var(--site-navigation-accentText)}
.site-navigation-marker-industrial{min-width:1.75rem;border-right:1px solid currentColor;padding-right:.42rem;font-family:ui-monospace,SFMono-Regular,monospace;font-size:.62rem;font-weight:800;letter-spacing:.06em}.site-navigation-panel-industrialBar{border-radius:0;border-top:4px solid var(--site-navigation-accent);background:linear-gradient(128deg,var(--site-navigation-background) 0 76%,color-mix(in srgb,var(--site-navigation-accent) 9%,var(--site-navigation-background)) 76%);box-shadow:0 28px 80px color-mix(in srgb,var(--site-navigation-text) 22%,transparent)}.site-navigation-industrial{grid-template-columns:minmax(0,1fr) minmax(15rem,22%)}.site-navigation-industrial .site-navigation-panel-main{padding:clamp(1.4rem,3vw,2.8rem)}.site-navigation-industrial .site-navigation-panel-heading{border-color:var(--site-navigation-text)}.site-navigation-industrial .site-navigation-panel-heading strong{font-size:clamp(1.5rem,3vw,2.75rem);font-weight:900;letter-spacing:-.035em;text-transform:uppercase}.site-navigation-tree-industrialBar{grid-template-columns:repeat(3,minmax(0,1fr));gap:0 1.25rem}.site-navigation-tree-industrialBar>.site-navigation-branch{border-top:1px solid var(--site-navigation-border);padding:.65rem 0}.site-navigation-tree-industrialBar .site-navigation-branch-link{border-radius:0;padding:.35rem 0;font-family:var(--site-heading-font,inherit);font-size:.78rem;text-transform:uppercase}.site-navigation-industrial-aside{position:relative;display:grid;min-width:0;align-content:space-between;overflow:hidden;border-left:1px solid var(--site-navigation-border);background:var(--site-navigation-surface);padding:1rem}.site-navigation-industrial-aside::before{position:absolute;top:-4rem;right:-5.5rem;width:10rem;height:12rem;transform:rotate(28deg);background:var(--site-navigation-accent);content:"";opacity:.94}.site-navigation-industrial-count{position:relative;z-index:1;max-width:70%;font-family:ui-monospace,SFMono-Regular,monospace;font-size:.62rem;font-weight:850;letter-spacing:.1em;text-transform:uppercase}.site-navigation-industrial-aside .site-navigation-feature{position:relative;z-index:1;min-height:13rem;margin:0;border-radius:0;background:var(--site-navigation-background)}

/* Alcatraz-inspired cinematic blueprint. */
.site-navigation-primary-cinematicOverlay{gap:clamp(.25rem,1.6vw,1.4rem)}.site-navigation-primary-cinematicOverlay .site-navigation-primary-trigger,.site-navigation-primary-cinematicOverlay .site-navigation-primary-link{position:relative;min-height:48px;border-radius:0;padding-inline:.55rem;color:var(--site-navigation-text);font-family:var(--site-heading-font,inherit);font-size:.75rem;font-weight:760;letter-spacing:.065em;text-shadow:0 1px 12px color-mix(in srgb,var(--site-navigation-background) 82%,transparent),0 0 2px var(--site-navigation-background);text-transform:uppercase}.site-navigation-primary-cinematicOverlay .site-navigation-primary-trigger::after,.site-navigation-primary-cinematicOverlay .site-navigation-primary-link::after{position:absolute;right:.5rem;bottom:.25rem;left:.5rem;height:1px;transform:scaleX(0);transform-origin:center;background:var(--site-navigation-accent);box-shadow:0 0 12px var(--site-navigation-accent);content:"";transition:transform .28s ease}.site-navigation-primary-cinematicOverlay .site-navigation-primary-trigger:hover::after,.site-navigation-primary-cinematicOverlay .site-navigation-primary-link:hover::after,.site-navigation-primary-cinematicOverlay .site-navigation-primary-item.is-open>.site-navigation-primary-trigger::after,.site-navigation-primary-cinematicOverlay .site-navigation-primary-item.is-active>.site-navigation-primary-link::after{transform:scaleX(1)}
.site-navigation-marker-cinematic{min-width:1.5rem;font-family:ui-monospace,SFMono-Regular,monospace;font-size:.58rem;letter-spacing:.09em;text-shadow:0 0 10px var(--site-navigation-accent)}.site-navigation-panel-cinematicOverlay{overflow:hidden;border-radius:2px;border-color:color-mix(in srgb,var(--site-navigation-accent) 38%,var(--site-navigation-border));background:radial-gradient(circle at 8% 8%,color-mix(in srgb,var(--site-navigation-accent) 18%,transparent),transparent 30%),radial-gradient(circle at 90% 78%,color-mix(in srgb,var(--site-navigation-accent) 10%,transparent),transparent 34%),color-mix(in srgb,var(--site-navigation-background) 96%,transparent);box-shadow:0 30px 90px color-mix(in srgb,var(--site-navigation-text) 28%,transparent),0 0 28px color-mix(in srgb,var(--site-navigation-accent) 10%,transparent);backdrop-filter:blur(28px) saturate(1.18)}.site-navigation-cinematic{position:relative;isolation:isolate;grid-template-columns:minmax(0,1.45fr) minmax(17rem,.55fr);min-height:24rem}.site-navigation-cinematic .site-navigation-panel-main{position:relative;z-index:1;padding:clamp(1.6rem,3.5vw,3.4rem)}.site-navigation-cinematic .site-navigation-panel-heading{align-items:start;border-bottom-color:color-mix(in srgb,var(--site-navigation-accent) 35%,var(--site-navigation-border))}.site-navigation-cinematic .site-navigation-panel-heading strong{font-size:clamp(1.7rem,3.5vw,3.4rem);font-weight:620;letter-spacing:-.045em}.site-navigation-tree-cinematicOverlay{grid-template-columns:repeat(2,minmax(0,1fr));gap:.25rem 1.5rem}.site-navigation-tree-cinematicOverlay>.site-navigation-branch{border-bottom:1px solid color-mix(in srgb,var(--site-navigation-text) 10%,var(--site-navigation-border));padding:.65rem 0}.site-navigation-tree-cinematicOverlay .site-navigation-branch-link{border-radius:0;padding:.5rem 0;font-family:var(--site-heading-font,inherit);font-size:.92rem;font-weight:620}.site-navigation-tree-cinematicOverlay .site-navigation-branch-link:hover{background:transparent;text-shadow:0 0 16px color-mix(in srgb,var(--site-navigation-accent) 45%,transparent);transform:translateX(5px)}.site-navigation-cinematic-feature{position:relative;z-index:1;display:flex;border-left:1px solid color-mix(in srgb,var(--site-navigation-accent) 28%,var(--site-navigation-border));padding:1rem}.site-navigation-cinematic-feature .site-navigation-feature{min-height:100%;width:100%;margin:0;border-radius:1px;background:linear-gradient(145deg,color-mix(in srgb,var(--site-navigation-accent) 14%,var(--site-navigation-surface)),var(--site-navigation-surface));box-shadow:inset 0 0 0 1px color-mix(in srgb,var(--site-navigation-accent) 20%,transparent)}.site-navigation-cinematic-orbit{position:absolute;z-index:0;top:-10rem;right:10%;width:28rem;height:28rem;border:1px solid color-mix(in srgb,var(--site-navigation-accent) 17%,transparent);border-radius:999px;box-shadow:0 0 70px color-mix(in srgb,var(--site-navigation-accent) 8%,transparent);pointer-events:none}

.site-navigation-mobile-industrialBar{gap:0;font-family:var(--site-heading-font,inherit)}.site-navigation-mobile-industrialBar>.site-navigation-mobile-item{border-top:1px solid var(--site-navigation-border)}.site-navigation-mobile-industrialBar>.site-navigation-mobile-item:last-child{border-bottom:1px solid var(--site-navigation-border)}.site-navigation-mobile-industrialBar .site-navigation-mobile-trigger,.site-navigation-mobile-industrialBar .site-navigation-mobile-link,.site-navigation-mobile-industrialBar .site-navigation-mobile-parent,.site-navigation-mobile-industrialBar .site-navigation-mobile-toggle{border-radius:0}.site-navigation-mobile-industrialBar>.site-navigation-mobile-item>.site-navigation-mobile-trigger,.site-navigation-mobile-industrialBar>.site-navigation-mobile-item>.site-navigation-mobile-link,.site-navigation-mobile-industrialBar>.site-navigation-mobile-item>.site-navigation-mobile-parent{min-height:56px;text-transform:uppercase;letter-spacing:.05em}.site-navigation-mobile-industrialBar .site-navigation-mobile-panel{margin:0;border-left:3px solid var(--site-navigation-accent);background:color-mix(in srgb,var(--site-navigation-surface) 65%,transparent);padding:.35rem 0 .35rem .65rem}.site-navigation-mobile-industrialBar .site-navigation-marker-industrial{color:var(--site-navigation-accent)}
.site-navigation-mobile-cinematicOverlay{gap:0}.site-navigation-mobile-cinematicOverlay>.site-navigation-mobile-item{border-bottom:1px solid color-mix(in srgb,var(--site-navigation-accent) 20%,var(--site-navigation-border))}.site-navigation-mobile-cinematicOverlay .site-navigation-mobile-trigger,.site-navigation-mobile-cinematicOverlay .site-navigation-mobile-link,.site-navigation-mobile-cinematicOverlay .site-navigation-mobile-parent,.site-navigation-mobile-cinematicOverlay .site-navigation-mobile-toggle{border-radius:0}.site-navigation-mobile-cinematicOverlay>.site-navigation-mobile-item>.site-navigation-mobile-trigger,.site-navigation-mobile-cinematicOverlay>.site-navigation-mobile-item>.site-navigation-mobile-link,.site-navigation-mobile-cinematicOverlay>.site-navigation-mobile-item>.site-navigation-mobile-parent{min-height:60px;font-family:var(--site-heading-font,inherit);font-size:1rem;font-weight:620;letter-spacing:.03em}.site-navigation-mobile-cinematicOverlay .site-navigation-mobile-panel{margin:0;border-left:1px solid var(--site-navigation-accent);background:radial-gradient(circle at 100% 0,color-mix(in srgb,var(--site-navigation-accent) 12%,transparent),transparent 45%);padding:.3rem 0 .5rem .7rem}.site-navigation-mobile-cinematicOverlay .site-navigation-marker-cinematic{color:var(--site-navigation-accent)}

@media(max-width:1240px) and (min-width:${SITE_NAVIGATION_MOBILE_BREAKPOINT_PX + 1}px){.web-site-navigation .site-navigation-primary{gap:.05rem}.web-site-navigation .site-navigation-primary-item{flex:none}.web-site-navigation .site-navigation-primary-marker{display:none}.web-site-navigation .site-navigation-primary-trigger,.web-site-navigation .site-navigation-primary-link{gap:.2rem;overflow:visible;padding-inline:clamp(.25rem,.55vw,.45rem);font-size:.7rem;letter-spacing:0}.web-site-navigation .site-navigation-primary-label{min-width:max-content;overflow:visible;text-overflow:clip}.web-site-navigation .site-navigation-primary-toggle{width:1.45rem;padding-inline:.25rem}[data-web-template=torque-service] .web-site-navigation[data-site-navigation-variant=industrialBar] .site-navigation-primary-industrialBar{transform:translateX(-12px)}[data-web-template=torque-service] .web-site-navigation[data-site-navigation-variant=industrialBar] .site-navigation-primary-industrialBar .site-navigation-primary-trigger,[data-web-template=torque-service] .web-site-navigation[data-site-navigation-variant=industrialBar] .site-navigation-primary-industrialBar .site-navigation-primary-link{padding-inline:.3375rem;letter-spacing:.03em}}
@media(max-width:1120px) and (min-width:${SITE_NAVIGATION_MOBILE_BREAKPOINT_PX + 1}px){.site-navigation-industrial{grid-template-columns:minmax(0,1fr) 14rem}.site-navigation-tree-industrialBar{grid-template-columns:repeat(2,minmax(0,1fr))}.site-navigation-cinematic{grid-template-columns:minmax(0,1fr) 15rem}.site-navigation-tree-cinematicOverlay{grid-template-columns:minmax(0,1fr)}}
@media(max-width:${SITE_NAVIGATION_MOBILE_BREAKPOINT_PX}px){.site-navigation-panel-wrap{display:none}.site-navigation-primary{display:none}}
@media(prefers-reduced-motion:reduce){.site-navigation-root *{scroll-behavior:auto!important;transition-duration:0s!important;animation-duration:0s!important}.site-navigation-cinematic-orbit{box-shadow:none}.web-site-navigation .site-navigation-branch-link:hover,.site-navigation-tree-cinematicOverlay .site-navigation-branch-link:hover{transform:none}}
`;

const CLEAN_NAVIGATION_CSS = `
/* Markerless Web variants distilled from the restaurant mastheads. */
.site-navigation-primary:is(
    .site-navigation-primary-minimalReveal,
    .site-navigation-primary-boldUnderline,
    .site-navigation-primary-editorialRule,
    .site-navigation-primary-playfulFrame,
    .site-navigation-primary-neonLine
) .site-navigation-primary-marker {
    display: none;
}

.site-navigation-primary:is(
    .site-navigation-primary-minimalReveal,
    .site-navigation-primary-boldUnderline,
    .site-navigation-primary-editorialRule,
    .site-navigation-primary-playfulFrame,
    .site-navigation-primary-neonLine
) :is(.site-navigation-primary-trigger, .site-navigation-primary-link) {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    border-radius: 0;
    background: transparent;
}

.site-navigation-primary:is(
    .site-navigation-primary-minimalReveal,
    .site-navigation-primary-boldUnderline,
    .site-navigation-primary-editorialRule,
    .site-navigation-primary-playfulFrame,
    .site-navigation-primary-neonLine
) :is(
    .site-navigation-primary-parent:hover,
    .site-navigation-primary-item.is-active > .site-navigation-primary-parent,
    .site-navigation-primary-item.is-open > .site-navigation-primary-parent
) {
    background: transparent;
}

.site-navigation-clean {
    display: block;
}

.site-navigation-clean .site-navigation-panel-main {
    padding: clamp(1.35rem, 2.8vw, 2.65rem);
}

.site-navigation-clean-heading {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.35rem;
    border-bottom: 1px solid var(--site-navigation-border);
    padding-bottom: 1rem;
}

.site-navigation-clean-heading strong {
    font-family: var(--site-heading-font, inherit);
    font-size: clamp(1.2rem, 2.2vw, 2rem);
    line-height: 1.05;
}

.site-navigation-clean-show-all {
    flex: none;
    color: var(--site-navigation-accent);
    font-size: .76rem;
    font-weight: 800;
}

.site-navigation-tree:is(
    .site-navigation-tree-minimalReveal,
    .site-navigation-tree-boldUnderline,
    .site-navigation-tree-editorialRule,
    .site-navigation-tree-playfulFrame,
    .site-navigation-tree-neonLine
) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: .35rem clamp(1rem, 2.4vw, 2rem);
}

/* Quiet text links that reveal a centred hairline. */
.site-navigation-primary-minimalReveal {
    gap: clamp(.7rem, 2vw, 2rem);
}

.site-navigation-primary-minimalReveal :is(.site-navigation-primary-trigger, .site-navigation-primary-link) {
    min-height: 46px;
    padding: .55rem .3rem;
    color: var(--site-navigation-muted);
    font-size: .82rem;
    font-weight: 680;
    letter-spacing: .02em;
    transition: color .2s ease, opacity .2s ease;
}

.site-navigation-primary-minimalReveal :is(.site-navigation-primary-trigger, .site-navigation-primary-link)::after {
    position: absolute;
    right: .2rem;
    bottom: .3rem;
    left: .2rem;
    height: 2px;
    transform: scaleX(0);
    transform-origin: center;
    background: var(--site-navigation-accent);
    content: "";
    transition: transform .24s cubic-bezier(.2, .7, .2, 1);
}

.site-navigation-primary-minimalReveal .site-navigation-primary-item:is(:hover, .is-active, .is-open) :is(.site-navigation-primary-trigger, .site-navigation-primary-link),
.site-navigation-primary-minimalReveal :is(.site-navigation-primary-trigger, .site-navigation-primary-link):focus-visible {
    background: transparent;
    color: var(--site-navigation-text);
}

.site-navigation-primary-minimalReveal .site-navigation-primary-item:is(:hover, .is-active, .is-open) :is(.site-navigation-primary-trigger, .site-navigation-primary-link)::after,
.site-navigation-primary-minimalReveal :is(.site-navigation-primary-trigger, .site-navigation-primary-link):focus-visible::after {
    transform: scaleX(1);
}

.site-navigation-panel-minimalReveal {
    border-radius: min(var(--site-navigation-radius), 10px);
    background: color-mix(in srgb, var(--site-navigation-background) 94%, transparent);
    backdrop-filter: blur(20px) saturate(1.08);
    animation: site-navigation-minimal-reveal .24s cubic-bezier(.2, .7, .2, 1) both;
}

.site-navigation-clean-minimalReveal .site-navigation-branch {
    border-bottom: 1px solid color-mix(in srgb, var(--site-navigation-text) 8%, var(--site-navigation-border));
    padding: .35rem 0;
}

.site-navigation-clean-minimalReveal .site-navigation-branch-link {
    border-radius: 0;
    background: transparent;
    padding: .45rem 0;
    font-weight: 680;
}

.site-navigation-clean-minimalReveal .site-navigation-branch-link:hover {
    background: transparent;
    transform: translateX(4px);
}

/* Poster-like, square navigation with a confident growing rule. */
.site-navigation-primary-boldUnderline {
    gap: clamp(.45rem, 1.4vw, 1.2rem);
}

.site-navigation-primary-boldUnderline :is(.site-navigation-primary-trigger, .site-navigation-primary-link) {
    min-height: 50px;
    padding: .65rem .45rem;
    font-family: var(--site-heading-font, inherit);
    font-size: .76rem;
    font-weight: 900;
    letter-spacing: .075em;
    text-transform: uppercase;
    transition: color .16s ease;
}

.site-navigation-primary-boldUnderline :is(.site-navigation-primary-trigger, .site-navigation-primary-link)::after {
    position: absolute;
    right: .4rem;
    bottom: .15rem;
    left: .4rem;
    height: 5px;
    transform: scaleX(0);
    transform-origin: left;
    background: var(--site-navigation-accent);
    content: "";
    transition: transform .2s ease;
}

.site-navigation-primary-boldUnderline .site-navigation-primary-item:is(:hover, .is-active, .is-open) :is(.site-navigation-primary-trigger, .site-navigation-primary-link),
.site-navigation-primary-boldUnderline :is(.site-navigation-primary-trigger, .site-navigation-primary-link):focus-visible {
    background: transparent;
    color: var(--site-navigation-text);
}

.site-navigation-primary-boldUnderline .site-navigation-primary-item:is(:hover, .is-active, .is-open) :is(.site-navigation-primary-trigger, .site-navigation-primary-link)::after,
.site-navigation-primary-boldUnderline :is(.site-navigation-primary-trigger, .site-navigation-primary-link):focus-visible::after {
    transform: scaleX(1);
}

.site-navigation-panel-boldUnderline {
    border: 2px solid var(--site-navigation-text);
    border-bottom: 6px solid var(--site-navigation-accent);
    border-radius: 0;
    background: var(--site-navigation-background);
    box-shadow: 0 24px 60px color-mix(in srgb, var(--site-navigation-text) 22%, transparent);
    animation: site-navigation-bold-reveal .2s cubic-bezier(.2, .7, .2, 1) both;
}

.site-navigation-clean-boldUnderline .site-navigation-clean-heading {
    border-bottom: 3px solid var(--site-navigation-text);
}

.site-navigation-clean-boldUnderline :is(.site-navigation-clean-heading strong, .site-navigation-branch-link) {
    font-family: var(--site-heading-font, inherit);
    font-weight: 900;
    letter-spacing: .04em;
    text-transform: uppercase;
}

.site-navigation-clean-boldUnderline .site-navigation-branch {
    border-top: 1px solid var(--site-navigation-border);
    padding: .35rem 0;
}

.site-navigation-clean-boldUnderline .site-navigation-branch-link {
    border-radius: 0;
    padding: .45rem .2rem;
}

.site-navigation-clean-boldUnderline .site-navigation-branch-link:hover {
    background: var(--site-navigation-accent);
    color: var(--site-navigation-accentText);
    transform: none;
}

/* Restrained editorial typography divided by fine rules. */
.site-navigation-primary-editorialRule {
    gap: 0;
}

.site-navigation-primary-editorialRule > .site-navigation-primary-item + .site-navigation-primary-item {
    border-left: 1px solid var(--site-navigation-border);
}

.site-navigation-primary-editorialRule :is(.site-navigation-primary-trigger, .site-navigation-primary-link) {
    min-height: 48px;
    padding: .6rem clamp(.7rem, 1.5vw, 1.25rem);
    font-size: .7rem;
    font-weight: 760;
    letter-spacing: .13em;
    text-transform: uppercase;
    transition: color .2s ease;
}

.site-navigation-primary-editorialRule :is(.site-navigation-primary-trigger, .site-navigation-primary-link)::after {
    position: absolute;
    right: clamp(.7rem, 1.5vw, 1.25rem);
    bottom: 0;
    left: clamp(.7rem, 1.5vw, 1.25rem);
    height: 2px;
    transform: scaleX(0);
    transform-origin: left;
    background: var(--site-navigation-accent);
    content: "";
    transition: transform .24s ease;
}

.site-navigation-primary-editorialRule .site-navigation-primary-item:is(:hover, .is-active, .is-open) :is(.site-navigation-primary-trigger, .site-navigation-primary-link),
.site-navigation-primary-editorialRule :is(.site-navigation-primary-trigger, .site-navigation-primary-link):focus-visible {
    background: transparent;
    color: var(--site-navigation-accent);
}

.site-navigation-primary-editorialRule .site-navigation-primary-item:is(:hover, .is-active, .is-open) :is(.site-navigation-primary-trigger, .site-navigation-primary-link)::after,
.site-navigation-primary-editorialRule :is(.site-navigation-primary-trigger, .site-navigation-primary-link):focus-visible::after {
    transform: scaleX(1);
}

.site-navigation-panel-editorialRule {
    border-radius: 0;
    background: var(--site-navigation-background);
    box-shadow: 0 20px 52px color-mix(in srgb, var(--site-navigation-text) 12%, transparent);
    animation: site-navigation-editorial-reveal .25s ease-out both;
}

.site-navigation-clean-editorialRule .site-navigation-clean-heading {
    align-items: center;
    border-block: 1px solid var(--site-navigation-border);
    padding-block: 1rem;
}

.site-navigation-clean-editorialRule .site-navigation-clean-heading strong {
    font-weight: 580;
    letter-spacing: -.015em;
}

.site-navigation-clean-editorialRule .site-navigation-branch {
    border-bottom: 1px solid var(--site-navigation-border);
    padding: .4rem 0;
}

.site-navigation-clean-editorialRule .site-navigation-branch-link {
    border-radius: 0;
    background: transparent;
    padding: .4rem 0;
    font-size: .79rem;
    font-weight: 650;
}

.site-navigation-clean-editorialRule .site-navigation-branch-link:hover {
    background: transparent;
    transform: translateX(3px);
}

/* Friendly outlined frame with a small offset accent shadow. */
.site-navigation-primary-playfulFrame {
    gap: .15rem;
    border: 2px solid var(--site-navigation-text);
    background: var(--site-navigation-background);
    padding: .15rem .35rem;
    box-shadow: 4px 4px 0 color-mix(in srgb, var(--site-navigation-accent) 42%, var(--site-navigation-surface));
}

.site-navigation-primary-playfulFrame :is(.site-navigation-primary-trigger, .site-navigation-primary-link) {
    min-height: 44px;
    padding: .55rem clamp(.55rem, 1.15vw, .95rem);
    font-size: .76rem;
    font-weight: 850;
    transition: color .18s ease, transform .18s ease;
}

.site-navigation-primary-playfulFrame :is(.site-navigation-primary-trigger, .site-navigation-primary-link)::after {
    position: absolute;
    right: .6rem;
    bottom: .25rem;
    left: .6rem;
    height: 3px;
    border-radius: 999px;
    transform: scaleX(0);
    transform-origin: center;
    background: var(--site-navigation-accent);
    content: "";
    transition: transform .2s ease;
}

.site-navigation-primary-playfulFrame .site-navigation-primary-item:is(:hover, .is-active, .is-open) :is(.site-navigation-primary-trigger, .site-navigation-primary-link),
.site-navigation-primary-playfulFrame :is(.site-navigation-primary-trigger, .site-navigation-primary-link):focus-visible {
    background: transparent;
    color: var(--site-navigation-accent);
    transform: translateY(-1px);
}

.site-navigation-primary-playfulFrame .site-navigation-primary-item:is(:hover, .is-active, .is-open) :is(.site-navigation-primary-trigger, .site-navigation-primary-link)::after,
.site-navigation-primary-playfulFrame :is(.site-navigation-primary-trigger, .site-navigation-primary-link):focus-visible::after {
    transform: scaleX(1);
}

.site-navigation-panel-playfulFrame {
    border: 2px solid var(--site-navigation-text);
    border-radius: min(var(--site-navigation-radius), 14px);
    background: var(--site-navigation-background);
    box-shadow: 7px 7px 0 color-mix(in srgb, var(--site-navigation-accent) 48%, var(--site-navigation-surface));
    animation: site-navigation-playful-reveal .22s cubic-bezier(.2, .8, .3, 1.15) both;
}

.site-navigation-clean-playfulFrame .site-navigation-clean-heading {
    border-bottom: 2px solid var(--site-navigation-text);
}

.site-navigation-clean-playfulFrame .site-navigation-clean-heading strong {
    font-weight: 850;
}

.site-navigation-clean-playfulFrame .site-navigation-branch {
    border-bottom: 1px solid var(--site-navigation-border);
    padding: .3rem 0;
}

.site-navigation-clean-playfulFrame .site-navigation-branch-link {
    padding: .45rem .5rem;
    font-weight: 740;
}

.site-navigation-clean-playfulFrame .site-navigation-branch-link:hover {
    background: color-mix(in srgb, var(--site-navigation-accent) 10%, transparent);
    transform: translate(2px, -1px);
}

/* Dark glass navigation with a directional glowing line, never an index. */
.site-navigation-primary-neonLine {
    gap: clamp(.35rem, 1.7vw, 1.45rem);
}

.site-navigation-primary-neonLine :is(.site-navigation-primary-trigger, .site-navigation-primary-link) {
    min-height: 48px;
    padding: .55rem .45rem;
    font-family: var(--site-heading-font, inherit);
    font-size: .74rem;
    font-weight: 760;
    letter-spacing: .07em;
    text-shadow: 0 1px 12px color-mix(in srgb, var(--site-navigation-background) 82%, transparent);
    text-transform: uppercase;
    transition: color .18s ease, text-shadow .18s ease;
}

.site-navigation-primary-neonLine :is(.site-navigation-primary-trigger, .site-navigation-primary-link)::after {
    position: absolute;
    right: .4rem;
    bottom: .2rem;
    left: .4rem;
    height: 2px;
    transform: scaleX(0);
    transform-origin: right;
    background: var(--site-navigation-accent);
    box-shadow: 0 0 12px var(--site-navigation-accent);
    content: "";
    transition: transform .24s ease;
}

.site-navigation-primary-neonLine .site-navigation-primary-item:is(:hover, .is-active, .is-open) :is(.site-navigation-primary-trigger, .site-navigation-primary-link),
.site-navigation-primary-neonLine :is(.site-navigation-primary-trigger, .site-navigation-primary-link):focus-visible {
    background: transparent;
    color: var(--site-navigation-text);
    text-shadow: 0 0 14px color-mix(in srgb, var(--site-navigation-accent) 38%, transparent);
}

.site-navigation-primary-neonLine .site-navigation-primary-item:is(:hover, .is-active, .is-open) :is(.site-navigation-primary-trigger, .site-navigation-primary-link)::after,
.site-navigation-primary-neonLine :is(.site-navigation-primary-trigger, .site-navigation-primary-link):focus-visible::after {
    transform: scaleX(1);
    transform-origin: left;
}

.site-navigation-panel-neonLine {
    border-color: color-mix(in srgb, var(--site-navigation-accent) 42%, var(--site-navigation-border));
    border-radius: 3px;
    background:
        radial-gradient(circle at 92% 12%, color-mix(in srgb, var(--site-navigation-accent) 13%, transparent), transparent 34%),
        color-mix(in srgb, var(--site-navigation-background) 94%, transparent);
    box-shadow:
        0 28px 80px color-mix(in srgb, var(--site-navigation-text) 25%, transparent),
        0 0 24px color-mix(in srgb, var(--site-navigation-accent) 12%, transparent);
    backdrop-filter: blur(24px) saturate(1.12);
    animation: site-navigation-neon-reveal .24s ease-out both;
}

.site-navigation-clean-neonLine .site-navigation-clean-heading {
    border-bottom-color: color-mix(in srgb, var(--site-navigation-accent) 38%, var(--site-navigation-border));
}

.site-navigation-clean-neonLine .site-navigation-clean-heading strong {
    font-weight: 620;
    letter-spacing: -.025em;
}

.site-navigation-clean-neonLine .site-navigation-branch {
    border-bottom: 1px solid color-mix(in srgb, var(--site-navigation-accent) 18%, var(--site-navigation-border));
    padding: .35rem 0;
}

.site-navigation-clean-neonLine .site-navigation-branch-link {
    border-radius: 0;
    background: transparent;
    padding: .45rem 0;
    font-family: var(--site-heading-font, inherit);
    font-weight: 620;
}

.site-navigation-clean-neonLine .site-navigation-branch-link:hover {
    background: transparent;
    text-shadow: 0 0 14px color-mix(in srgb, var(--site-navigation-accent) 45%, transparent);
    transform: translateX(5px);
}

@keyframes site-navigation-minimal-reveal {
    from { opacity: 0; transform: translateY(-8px); }
    to { opacity: 1; transform: none; }
}

@keyframes site-navigation-bold-reveal {
    from { clip-path: inset(0 100% 0 0); opacity: .65; }
    to { clip-path: inset(0); opacity: 1; }
}

@keyframes site-navigation-editorial-reveal {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: none; }
}

@keyframes site-navigation-playful-reveal {
    from { opacity: 0; transform: translate(-4px, -4px) scale(.985); }
    to { opacity: 1; transform: none; }
}

@keyframes site-navigation-neon-reveal {
    from { filter: blur(5px); opacity: 0; transform: translateY(-6px); }
    to { filter: none; opacity: 1; transform: none; }
}

/* Mobile keeps the same character with large, plain text targets. */
.site-navigation-mobile-minimalReveal {
    gap: 0;
}

.site-navigation-mobile-minimalReveal > .site-navigation-mobile-item {
    border-bottom: 1px solid color-mix(in srgb, var(--site-navigation-text) 11%, var(--site-navigation-border));
}

.site-navigation-mobile-minimalReveal > .site-navigation-mobile-item > :is(.site-navigation-mobile-trigger, .site-navigation-mobile-link, .site-navigation-mobile-parent) {
    min-height: 56px;
}

.site-navigation-mobile-minimalReveal :is(.site-navigation-mobile-trigger, .site-navigation-mobile-link, .site-navigation-mobile-parent, .site-navigation-mobile-toggle) {
    border-radius: 0;
}

.site-navigation-mobile-minimalReveal .site-navigation-mobile-panel {
    margin: 0;
    padding: .25rem 0 .45rem .8rem;
    animation: site-navigation-minimal-mobile .2s ease-out both;
}

.site-navigation-mobile-boldUnderline {
    gap: 0;
    border-block: 4px solid var(--site-navigation-accent);
}

.site-navigation-mobile-boldUnderline > .site-navigation-mobile-item {
    border-bottom: 1px solid var(--site-navigation-border);
}

.site-navigation-mobile-boldUnderline > .site-navigation-mobile-item > :is(.site-navigation-mobile-trigger, .site-navigation-mobile-link, .site-navigation-mobile-parent) {
    min-height: 58px;
    font-family: var(--site-heading-font, inherit);
    font-weight: 900;
    letter-spacing: .06em;
    text-transform: uppercase;
}

.site-navigation-mobile-boldUnderline :is(.site-navigation-mobile-trigger, .site-navigation-mobile-link, .site-navigation-mobile-parent, .site-navigation-mobile-toggle) {
    border-radius: 0;
}

.site-navigation-mobile-boldUnderline .site-navigation-mobile-panel {
    margin: 0;
    border-left: 4px solid var(--site-navigation-accent);
    padding: .3rem 0 .45rem .75rem;
    animation: site-navigation-bold-mobile .18s ease-out both;
}

.site-navigation-mobile-editorialRule {
    gap: 0;
    border-top: 1px solid var(--site-navigation-border);
}

.site-navigation-mobile-editorialRule > .site-navigation-mobile-item {
    border-bottom: 1px solid var(--site-navigation-border);
}

.site-navigation-mobile-editorialRule > .site-navigation-mobile-item > :is(.site-navigation-mobile-trigger, .site-navigation-mobile-link, .site-navigation-mobile-parent) {
    min-height: 54px;
    font-size: .78rem;
    font-weight: 760;
    letter-spacing: .1em;
    text-transform: uppercase;
}

.site-navigation-mobile-editorialRule :is(.site-navigation-mobile-trigger, .site-navigation-mobile-link, .site-navigation-mobile-parent, .site-navigation-mobile-toggle) {
    border-radius: 0;
}

.site-navigation-mobile-editorialRule .site-navigation-mobile-panel {
    margin: 0;
    border-left: 1px solid var(--site-navigation-accent);
    padding: .25rem 0 .5rem .9rem;
    animation: site-navigation-editorial-mobile .22s ease-out both;
}

.site-navigation-mobile-playfulFrame {
    gap: 0;
    border: 2px solid var(--site-navigation-text);
    padding: .25rem;
    box-shadow: 5px 5px 0 color-mix(in srgb, var(--site-navigation-accent) 45%, var(--site-navigation-surface));
}

.site-navigation-mobile-playfulFrame > .site-navigation-mobile-item + .site-navigation-mobile-item {
    border-top: 1px solid var(--site-navigation-border);
}

.site-navigation-mobile-playfulFrame > .site-navigation-mobile-item > :is(.site-navigation-mobile-trigger, .site-navigation-mobile-link, .site-navigation-mobile-parent) {
    min-height: 56px;
    font-weight: 840;
}

.site-navigation-mobile-playfulFrame :is(.site-navigation-mobile-trigger, .site-navigation-mobile-link, .site-navigation-mobile-parent, .site-navigation-mobile-toggle) {
    border-radius: min(var(--site-navigation-radius), 8px);
}

.site-navigation-mobile-playfulFrame .site-navigation-mobile-panel {
    margin: .15rem .4rem .5rem;
    border-left: 2px solid var(--site-navigation-accent);
    padding: .2rem 0 .35rem .65rem;
    animation: site-navigation-playful-mobile .2s cubic-bezier(.2, .8, .3, 1.15) both;
}

.site-navigation-mobile-neonLine {
    gap: 0;
}

.site-navigation-mobile-neonLine > .site-navigation-mobile-item {
    border-bottom: 1px solid color-mix(in srgb, var(--site-navigation-accent) 22%, var(--site-navigation-border));
}

.site-navigation-mobile-neonLine > .site-navigation-mobile-item > :is(.site-navigation-mobile-trigger, .site-navigation-mobile-link, .site-navigation-mobile-parent) {
    min-height: 60px;
    font-family: var(--site-heading-font, inherit);
    font-size: 1rem;
    font-weight: 620;
    letter-spacing: .035em;
    text-shadow: 0 0 12px color-mix(in srgb, var(--site-navigation-accent) 18%, transparent);
}

.site-navigation-mobile-neonLine :is(.site-navigation-mobile-trigger, .site-navigation-mobile-link, .site-navigation-mobile-parent, .site-navigation-mobile-toggle) {
    border-radius: 0;
}

.site-navigation-mobile-neonLine .site-navigation-mobile-panel {
    margin: 0;
    border-left: 2px solid var(--site-navigation-accent);
    background: radial-gradient(circle at 100% 0, color-mix(in srgb, var(--site-navigation-accent) 11%, transparent), transparent 44%);
    padding: .3rem 0 .5rem .75rem;
    animation: site-navigation-neon-mobile .22s ease-out both;
}

@keyframes site-navigation-minimal-mobile {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: none; }
}

@keyframes site-navigation-bold-mobile {
    from { opacity: 0; transform: translateX(-8px); }
    to { opacity: 1; transform: none; }
}

@keyframes site-navigation-editorial-mobile {
    from { clip-path: inset(0 0 100% 0); opacity: .65; }
    to { clip-path: inset(0); opacity: 1; }
}

@keyframes site-navigation-playful-mobile {
    from { opacity: 0; transform: translate(-3px, -3px); }
    to { opacity: 1; transform: none; }
}

/* A connected Client Portal is a system action, not another content page. */
.web-site-navigation [data-navigation-item-type="clientPortal"] {
    border-color: color-mix(in srgb, var(--site-navigation-accent) 82%, var(--site-navigation-border)) !important;
    border-radius: var(--site-navigation-radius) !important;
    background: var(--site-navigation-accent) !important;
    box-shadow: 0 8px 22px color-mix(in srgb, var(--site-navigation-accent) 22%, transparent);
    color: var(--site-navigation-accentText) !important;
}

.web-site-navigation [data-navigation-item-type="clientPortal"]:hover,
.web-site-navigation [data-navigation-item-type="clientPortal"]:focus-visible {
    background: color-mix(in srgb, var(--site-navigation-accent) 86%, var(--site-navigation-background)) !important;
    color: var(--site-navigation-accentText) !important;
    transform: translateY(-1px);
}

.web-site-navigation [data-navigation-item-type="clientPortal"]::after {
    display: none !important;
}

@keyframes site-navigation-neon-mobile {
    from { filter: blur(4px); opacity: 0; }
    to { filter: none; opacity: 1; }
}

@media (max-width: 1120px) and (min-width: ${SITE_NAVIGATION_MOBILE_BREAKPOINT_PX + 1}px) {
    .site-navigation-tree:is(
        .site-navigation-tree-minimalReveal,
        .site-navigation-tree-boldUnderline,
        .site-navigation-tree-editorialRule,
        .site-navigation-tree-playfulFrame,
        .site-navigation-tree-neonLine
    ) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (prefers-reduced-motion: reduce) {
    .site-navigation-root :is(
        .site-navigation-panel-minimalReveal,
        .site-navigation-panel-boldUnderline,
        .site-navigation-panel-editorialRule,
        .site-navigation-panel-playfulFrame,
        .site-navigation-panel-neonLine,
        .site-navigation-mobile-panel
    ) {
        animation: none !important;
        filter: none !important;
    }

    .site-navigation-root :is(
        .site-navigation-primary-playfulFrame .site-navigation-primary-trigger,
        .site-navigation-primary-playfulFrame .site-navigation-primary-link,
        .site-navigation-branch-link,
        .site-navigation-mobile-trigger,
        .site-navigation-mobile-link
    ) {
        transform: none !important;
    }
}
`;

export function SiteNavigationStyles() {
    return (
        <style data-site-navigation-styles="true">
            {`${NAVIGATION_CSS}\n${CLEAN_NAVIGATION_CSS}`}
        </style>
    );
}

export function SiteNavigation({
    activeItemUuid,
    className,
    includeStyles = true,
    items,
    labels,
    mode,
    preservePanelOnLinkClick = false,
    previewOpenItemUuid,
    readOnly = false,
    renderLink = DefaultLink,
    settings,
    staticPreview = false,
    style,
}: SiteNavigationProps) {
    if (items.length === 0) {
        return null;
    }

    return (
        <nav
            aria-label={labels.navigation}
            className={classes("site-navigation-root", className)}
            data-site-navigation-alignment={settings.alignment}
            data-site-navigation-mode={mode}
            data-site-navigation-open-mode={settings.openMode}
            data-site-navigation-read-only={readOnly ? "true" : undefined}
            data-site-navigation-root="true"
            data-site-navigation-source={settings.source}
            data-site-navigation-static-preview={staticPreview ? "true" : undefined}
            data-site-navigation-signature={neutralNavigationSignature(settings.variant)}
            data-site-navigation-theme={settings.theme}
            data-site-navigation-variant={settings.variant}
            onClickCapture={readOnly ? (event) => {
                event.preventDefault();
                event.stopPropagation();
            } : undefined}
            style={style as CSSProperties}
        >
            {includeStyles ? <SiteNavigationStyles /> : null}
            {mode === "desktop" ? (
                settings.variant === "lagunaClassic" ? (
                    <LagunaClassicDesktopNavigation
                        activeItemUuid={activeItemUuid}
                        items={items}
                        labels={labels}
                        preservePanelOnLinkClick={preservePanelOnLinkClick}
                        previewOpenItemUuid={previewOpenItemUuid}
                        readOnly={readOnly}
                        renderLink={renderLink}
                        settings={settings}
                        staticPreview={staticPreview}
                    />
                ) : <DesktopNavigation
                    activeItemUuid={activeItemUuid}
                    items={items}
                    labels={labels}
                    preservePanelOnLinkClick={preservePanelOnLinkClick}
                    previewOpenItemUuid={previewOpenItemUuid}
                    readOnly={readOnly}
                    renderLink={renderLink}
                    settings={settings}
                    staticPreview={staticPreview}
                />
            ) : settings.variant === "lagunaClassic" ? (
                <LagunaClassicMobileNavigation
                    activeItemUuid={activeItemUuid}
                    items={items}
                    labels={labels}
                    previewOpenItemUuid={previewOpenItemUuid}
                    readOnly={readOnly}
                    renderLink={renderLink}
                />
            ) : (
                <MobileNavigation
                    activeItemUuid={activeItemUuid}
                    items={items}
                    labels={labels}
                    previewOpenItemUuid={previewOpenItemUuid}
                    readOnly={readOnly}
                    renderLink={renderLink}
                    settings={settings}
                />
            )}
        </nav>
    );
}
